import { useState, useEffect, useRef, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { PropertyFormData, PropertyCategory, DescriptionLanguage, FurnishedType, PropertyPhoto, PhotoEnhancementOptions, ResidentialType, CommercialType, LandType, PhotoEnhancementTemplate } from '@/types/property';
import { useAuth } from '@/hooks/useAuth';
import { useGeoCurrency } from '@/hooks/useGeoCurrency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { CityAutocomplete } from '@/components/CityAutocomplete';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Home, Building2, MapPin, Tag, Bed, Bath, Ruler, DollarSign, 
  Sparkles, Globe, Smile, UserCircle, ArrowRight, ArrowLeft,
  Pencil,
  Bot, Check, SkipForward, Link, Loader2, Upload, X,
  Image as ImageIcon, Camera, Cable, Sun, Palette, Contrast,
  Eye, CloudSun, Eraser, Trash2, Settings2, ChevronDown, ScanSearch, Square,
  ChevronUp, Mail, Ban, Mountain, Droplets, Stamp, Wand2,
  FileText, Zap, PartyPopper, Rocket, Stars, Save, Info, Sunset, Video, User,
  Crown, TrendingUp, AlertCircle, Hash, MessageSquare, MonitorSmartphone, Lamp
  , Clapperboard
} from 'lucide-react';
import { scrapeListingFromUrl, downloadImagesAsFiles, DEFAULT_MAX_IMPORT_PHOTOS, EXTENDED_MAX_IMPORT_PHOTOS } from '@/lib/scrapeListing';
import { toast } from '@/hooks/use-toast';

import { useBilling } from '@/hooks/useBilling';
import { useCreateListing } from '@/contexts/CreateListingContext';
import { TIER_CONFIGS, getRemainingQuota, SubscriptionTier } from '@/types/billing';
import { getFeatureCategories, furnishedOptions, detectFeaturesFromDescription, normalizeFeatures } from '@/lib/featureCategories';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { getDataFastAttribution } from '@/lib/datafast';
import { analyzePhotos, normalizeAnalysisFeatures, PhotoAnalysisResult } from '@/lib/analyzePhotos';
import { getMarketConfig } from '@/lib/markets';
import { formatPriceForMarket } from '@/lib/formatMarket';

interface UnifiedListingChatProps {
  data: PropertyFormData;
  onChange: (data: PropertyFormData) => void;
  photos: PropertyPhoto[];
  onPhotosChange: (photos: PropertyPhoto[]) => void;
  onGenerate: (photos: PropertyPhoto[], processInBackground?: boolean) => void;
  onStopEnhancement?: () => void;
  isGenerating: boolean;
  processingPhotoId?: string;
  processedCount?: number;
  userEmail?: string;
  generationPhase?: 'enhancing' | 'generating-description' | 'generating-media' | 'uploading-photos' | 'complete';
  mediaGenerationStatus?: {
    profilePicture: 'idle' | 'generating' | 'complete' | 'error';
    video: 'idle' | 'generating' | 'complete' | 'error';
  };
}

interface ChatMessage {
  id: string;
  type: 'bot' | 'user';
  content: string;
  step: number;
  isQuickFlow?: boolean;
}

// Property type options
const residentialTypes = [
  { value: 'apartment', label: 'Apartment', emoji: '🏢' },
  { value: 'house', label: 'House', emoji: '🏠' },
  { value: 'villa', label: 'Villa', emoji: '🏡' },
  { value: 'maisonette', label: 'Maisonette', emoji: '🏘️' },
  { value: 'townhouse', label: 'Townhouse', emoji: '🏘️' },
  { value: 'studio', label: 'Studio', emoji: '🛏️' },
  { value: 'penthouse', label: 'Penthouse', emoji: '✨' },
];

const commercialTypes = [
  { value: 'office', label: 'Office', emoji: '💼' },
  { value: 'shop', label: 'Shop', emoji: '🏪' },
  { value: 'warehouse', label: 'Warehouse', emoji: '📦' },
  { value: 'restaurant', label: 'Restaurant', emoji: '🍽️' },
  { value: 'hotel', label: 'Hotel', emoji: '🏨' },
  { value: 'building', label: 'Building', emoji: '🏗️' },
];

const landTypes = [
  { value: 'residential-plot', label: 'Residential Plot', emoji: '🏡' },
  { value: 'commercial-plot', label: 'Commercial Plot', emoji: '🏗️' },
  { value: 'agricultural', label: 'Agricultural', emoji: '🌾' },
  { value: 'industrial', label: 'Industrial', emoji: '🏭' },
];

// Popular global cities shown as quick-pick chips when a user has no recent
// city history. A geographically diverse mix so the default never feels
// region-locked. Once a user creates their first listing, their actual recent
// cities replace these chips.
const popularCities = [
  'New York, United States',
  'London, United Kingdom',
  'Los Angeles, United States',
  'Toronto, Canada',
  'Dubai, United Arab Emirates',
  'Madrid, Spain',
  'Sydney, Australia',
  'Singapore',
];

const cleanAreaSizeInput = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const compact = raw.replace(/\s/g, '').replace(/[^0-9.,]/g, '');
  if (!compact) return '';

  const commaIndex = compact.lastIndexOf(',');
  const dotIndex = compact.lastIndexOf('.');
  const lastSeparatorIndex = Math.max(commaIndex, dotIndex);

  if (lastSeparatorIndex === -1) return compact.replace(/\D/g, '');

  const integerPart = compact.slice(0, lastSeparatorIndex);
  const decimalPart = compact.slice(lastSeparatorIndex + 1);
  const separatorCount = (compact.match(/[,.]/g) || []).length;

  if (separatorCount === 1 && decimalPart.length === 3 && integerPart.length <= 3) {
    return compact.replace(/\D/g, '');
  }

  const whole = integerPart.replace(/\D/g, '');
  const decimal = decimalPart.replace(/\D/g, '');

  return decimal ? `${whole}.${decimal}` : whole;
};

// Stored square-meter value <-> display value in the user's market unit (sqm or sqft).
// Listings persist canonical sqm; we only convert for display/edit in sqft markets.
const SQM_TO_SQFT = 10.7639;
const sqmToDisplayArea = (value: unknown, unit: 'sqft' | 'sqm'): string => {
  const cleaned = cleanAreaSizeInput(value);
  if (!cleaned) return '';
  if (unit !== 'sqft') return cleaned;
  const num = parseFloat(cleaned);
  if (!isFinite(num)) return cleaned;
  return String(Math.round(num * SQM_TO_SQFT));
};
const displayAreaToSqm = (value: unknown, unit: 'sqft' | 'sqm'): string => {
  const cleaned = cleanAreaSizeInput(value);
  if (!cleaned) return '';
  if (unit !== 'sqft') return cleaned;
  const num = parseFloat(cleaned);
  if (!isFinite(num)) return cleaned;
  return String(Math.round(num / SQM_TO_SQFT));
};

const conditionOptions = [
  { value: 'new', label: 'New Build', emoji: '✨' },
  { value: 'renovated', label: 'Renovated', emoji: '🔧' },
  { value: 'good', label: 'Good', emoji: '👍' },
  { value: 'needs-renovation', label: 'Needs Work', emoji: '🛠️' },
];

type StagingMode = 'none' | 'decorative' | 'full';

const getStagingMode = (
  photoId: string,
  decorativeIds: Set<string>,
  fullIds: Set<string>,
): StagingMode => {
  if (fullIds.has(photoId)) return 'full';
  if (decorativeIds.has(photoId)) return 'decorative';
  return 'none';
};

const setIdsForMode = (
  photoId: string,
  mode: StagingMode,
  setDecorativeIds: Dispatch<SetStateAction<Set<string>>>,
  setFullIds: Dispatch<SetStateAction<Set<string>>>,
) => {
  setDecorativeIds(prev => {
    const next = new Set(prev);
    if (mode === 'decorative') next.add(photoId);
    else next.delete(photoId);
    return next;
  });
  setFullIds(prev => {
    const next = new Set(prev);
    if (mode === 'full') next.add(photoId);
    else next.delete(photoId);
    return next;
  });
};

// Popular languages shown by default
const popularLanguages = [
  { value: 'en' as DescriptionLanguage, label: 'English', flag: '🇬🇧' },
  { value: 'es' as DescriptionLanguage, label: 'Spanish', flag: '🇪🇸' },
  { value: 'fr' as DescriptionLanguage, label: 'French', flag: '🇫🇷' },
  { value: 'de' as DescriptionLanguage, label: 'German', flag: '🇩🇪' },
  { value: 'el' as DescriptionLanguage, label: 'Greek', flag: '🇬🇷' },
  { value: 'ru' as DescriptionLanguage, label: 'Russian', flag: '🇷🇺' },
  { value: 'ar' as DescriptionLanguage, label: 'Arabic', flag: '🇸🇦' },
  { value: 'he' as DescriptionLanguage, label: 'Hebrew', flag: '🇮🇱' },
];

// All other languages
const moreLanguages = [
  { value: 'it' as DescriptionLanguage, label: 'Italian', flag: '🇮🇹' },
  { value: 'pt' as DescriptionLanguage, label: 'Portuguese', flag: '🇵🇹' },
  { value: 'nl' as DescriptionLanguage, label: 'Dutch', flag: '🇳🇱' },
  { value: 'pl' as DescriptionLanguage, label: 'Polish', flag: '🇵🇱' },
  { value: 'tr' as DescriptionLanguage, label: 'Turkish', flag: '🇹🇷' },
  { value: 'zh' as DescriptionLanguage, label: 'Chinese', flag: '🇨🇳' },
  { value: 'ja' as DescriptionLanguage, label: 'Japanese', flag: '🇯🇵' },
  { value: 'ko' as DescriptionLanguage, label: 'Korean', flag: '🇰🇷' },
  { value: 'hi' as DescriptionLanguage, label: 'Hindi', flag: '🇮🇳' },
  { value: 'th' as DescriptionLanguage, label: 'Thai', flag: '🇹🇭' },
  { value: 'vi' as DescriptionLanguage, label: 'Vietnamese', flag: '🇻🇳' },
  { value: 'sv' as DescriptionLanguage, label: 'Swedish', flag: '🇸🇪' },
  { value: 'no' as DescriptionLanguage, label: 'Norwegian', flag: '🇳🇴' },
  { value: 'da' as DescriptionLanguage, label: 'Danish', flag: '🇩🇰' },
  { value: 'fi' as DescriptionLanguage, label: 'Finnish', flag: '🇫🇮' },
  { value: 'uk' as DescriptionLanguage, label: 'Ukrainian', flag: '🇺🇦' },
  { value: 'cs' as DescriptionLanguage, label: 'Czech', flag: '🇨🇿' },
  { value: 'ro' as DescriptionLanguage, label: 'Romanian', flag: '🇷🇴' },
  { value: 'hu' as DescriptionLanguage, label: 'Hungarian', flag: '🇭🇺' },
  { value: 'bg' as DescriptionLanguage, label: 'Bulgarian', flag: '🇧🇬' },
];

const allLanguages = [...popularLanguages, ...moreLanguages];

// Enhancement is now simplified - AI has creative freedom with custom prompting only

// Photo limits - can be 10 (default) or 20 (extended plan option)

// Flow steps
type FlowStep = 
  | 'welcome' 
  | 'method' 
  | 'import-url'
  | 'import-review'
  | 'category' 
  | 'listing-type' 
  | 'property-type' 
  | 'location' 
  | 'details' 
  | 'furnished'
  | 'features-quick'
  | 'features-full'
  | 'highlights'
  | 'description-settings'  // NEW: Choose saved profile settings or customize
  | 'description-customize' // NEW: Customize description tone, length, etc.
  | 'languages'
  | 'photos'
  | 'enhancement-style'  // NEW: Choose saved profile style or customize
  | 'enhancement-choice'
  | 'photo-instructions'
  | 'enhance-options'
  | 'media-generation'
  | 'watermark-options'
  | 'advanced-settings'
  | 'final-review'
  | 'generating';

// Photo enhancement presets with actual settings that affect the enhancement
// These settings are passed to the edge function to create VISIBLY DIFFERENT results
const photoEnhancementPresets = [
  {
    id: 'natural',
    name: 'Natural & Clean',
    description: 'Subtle improvements, authentic look with minimal processing',
    icon: '🏡',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    hoverBg: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/40',
    settings: {
      brightness: 5,      // Subtle brightness lift
      contrast: 5,        // Minimal contrast
      saturation: 10,     // Natural colors
      clarity: 15,        // Soft details
      shadows: 10,        // Light shadow lift
      highlights: -5,     // Minimal highlight recovery
      warmth: 0,          // Neutral temperature
    },
  },
  {
    id: 'bright_airy',
    name: 'Bright & Airy',
    description: 'Light, fresh, open feeling - great for modern spaces',
    icon: '☀️',
    bgColor: 'bg-sky-50 dark:bg-sky-950/30',
    borderColor: 'border-sky-200 dark:border-sky-800',
    hoverBg: 'hover:bg-sky-100 dark:hover:bg-sky-900/40',
    settings: {
      brightness: 25,     // Significant brightness boost
      contrast: 10,       // Light contrast
      saturation: 15,     // Fresh colors
      clarity: 20,        // Clean details
      shadows: 30,        // Strong shadow lift for airy feel
      highlights: -15,    // Recover windows
      warmth: -5,         // Slightly cool for freshness
    },
  },
  {
    id: 'warm_inviting',
    name: 'Warm & Inviting',
    description: 'Cozy atmosphere with golden tones - perfect for homey spaces',
    icon: '🔥',
    bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    borderColor: 'border-orange-200 dark:border-orange-800',
    hoverBg: 'hover:bg-orange-100 dark:hover:bg-orange-900/40',
    settings: {
      brightness: 12,     // Moderate brightness
      contrast: 15,       // Good contrast
      saturation: 25,     // Rich, warm colors
      clarity: 20,        // Clear details
      shadows: 20,        // Balanced shadows
      highlights: -10,    // Balanced highlights
      warmth: 20,         // WARM GOLDEN TONES - key differentiator!
    },
  },
  {
    id: 'magazine_ready',
    name: 'Magazine Ready',
    description: 'High-impact, vivid professional look - publication quality',
    icon: '✨',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    borderColor: 'border-purple-200 dark:border-purple-800',
    hoverBg: 'hover:bg-purple-100 dark:hover:bg-purple-900/40',
    settings: {
      brightness: 18,     // Strong brightness
      contrast: 25,       // Punchy contrast
      saturation: 35,     // Vivid colors
      clarity: 35,        // Sharp details
      shadows: 25,        // Good shadow lift
      highlights: -20,    // Strong highlight recovery
      warmth: 0,          // Neutral for professional look
    },
  },
] as const;

type PhotoPresetId = typeof photoEnhancementPresets[number]['id'];

const greetings = [
  "Hey there! 👋 Ready to create a stunning listing?",
  "Welcome back! 🏠 Let's make your property shine!",
  "Hi! ✨ Let's create something amazing together!",
];

export function UnifiedListingChat({
  data,
  onChange,
  photos,
  onPhotosChange,
  onGenerate,
  onStopEnhancement,
  isGenerating,
  processingPhotoId,
  processedCount,
  userEmail,
  generationPhase = 'enhancing',
  mediaGenerationStatus = { profilePicture: 'idle', video: 'idle' }
}: UnifiedListingChatProps) {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const { currency } = useGeoCurrency();
  const { consumePendingImportUrl } = useCreateListing();
  const checkoutCurrency = currency;

  const [currentStep, setCurrentStep] = useState<FlowStep>('welcome');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Enhancement style state
  const [useProfileStyle, setUseProfileStyle] = useState<boolean | null>(null);
  const [selectedPresetId, setSelectedPresetId] = useState<PhotoPresetId | null>(null);
  
  // Description settings state
  const [useProfileDescriptionSettings, setUseProfileDescriptionSettings] = useState<boolean | null>(null);
  
  // Photo enhancement state
  const [enhancementOptions, setEnhancementOptions] = useState<PhotoEnhancementOptions>({ addDecorations: false, goldenHourMode: false });
  const [customPrompt, setCustomPrompt] = useState('');
  const [processInBackground, setProcessInBackground] = useState(false);
  const [perPhotoPromptsEnabled, setPerPhotoPromptsEnabled] = useState(false);
  const [perPhotoPrompts, setPerPhotoPrompts] = useState<Record<string, string>>({});
  const [showPerPhotoEditor, setShowPerPhotoEditor] = useState(false);
  const [editingPhotoIndex, setEditingPhotoIndex] = useState(0);
  const [stagingPhotoIds, setStagingPhotoIds] = useState<Set<string>>(new Set());
  const [goldenHourPhotoIds, setGoldenHourPhotoIds] = useState<Set<string>>(new Set());
  // Full Virtual Staging (empty rooms → furnished with AI-generated furniture).
  // Separate quota from Virtual Decoration. Per-photo style stored alongside.
  const [fullStagingPhotoIds, setFullStagingPhotoIds] = useState<Set<string>>(new Set());
  const [fullStagingStyles, setFullStagingStyles] = useState<Record<string, 'modern' | 'scandinavian' | 'coastal' | 'luxury' | 'minimalist'>>({});
  // Global style applied to every photo flagged for Full Virtual Staging.
  const [fullStagingGlobalStyle, setFullStagingGlobalStyle] = useState<'modern' | 'scandinavian' | 'coastal' | 'luxury' | 'minimalist'>('modern');
  // Per-photo room type override for Full Virtual Staging. The model
  // mis-classifies empty rooms often (e.g. bedroom furnished as living room),
  // so we ask the user to confirm the room type for every photo they mark
  // for full staging. Defaults to 'living' when the user hasn't picked yet.
  type FullStagingRoomType = NonNullable<PhotoEnhancementOptions['fullStagingRoomType']>;
  const [fullStagingRoomTypes, setFullStagingRoomTypes] = useState<Record<string, FullStagingRoomType>>({});
  const isAnyStagingActive = stagingPhotoIds.size > 0 || fullStagingPhotoIds.size > 0;
  const isAnyEnhancementEffectActive = isAnyStagingActive || goldenHourPhotoIds.size > 0;
  // Cinematic AI Video ($5 add-on). Same pattern as the 20-photo add-on:
  // when the user toggles it on we open Stripe in a new tab and poll for
  // the webhook to credit the profile. Once credited, `wantsCinematicVideo`
  // is set — the parent consumes the credit when the listing is saved.
  const [wantsCinematicVideo, setWantsCinematicVideo] = useState(false);
  const [isCinematicCheckoutLoading, setIsCinematicCheckoutLoading] = useState(false);
  const [cinematicPaymentPending, setCinematicPaymentPending] = useState(false);
  
  // Billing state - for tier-gated features and usage tracking
  const { billing, tierConfig, checkFeature, canCreateNewListing, isSubscriber } = useBilling();
  const canUseWatermark = billing?.subscriptionTier === 'growth' || billing?.subscriptionTier === 'studio';
  
  // Tier display helpers
  const tierIcons: Record<SubscriptionTier, React.ElementType> = {
    free: Zap,
    starter: TrendingUp,
    growth: Rocket,
    studio: Crown,
    one_off_kit: Zap,
  };

  const TierIcon = billing ? (tierIcons[billing.subscriptionTier] || TrendingUp) : TrendingUp;
  
  // Calculate usage/limits for display
  // Use tier config as authoritative source for limits (not database value which may be stale)
  const activeListingsLimit = tierConfig?.activeListingsLimit ?? null;
  const propertiesRemaining = activeListingsLimit !== null 
    ? Math.max(0, activeListingsLimit - (billing?.activeListingsCount || 0))
    : 'unlimited';
  const reeditsRemaining = billing ? getRemainingQuota(billing, 'reedits') : 0;
  const reeditsLimit = tierConfig?.monthlyLimits?.reedits ?? 0;
  const reeditUsagePercent = reeditsLimit > 0 && typeof reeditsRemaining === 'number'
    ? Math.min(100, Math.max(0, ((reeditsLimit - reeditsRemaining) / reeditsLimit) * 100))
    : 0;
  
  const ReeditQuotaBanner = ({ compact = false }: { compact?: boolean }) => {
    if (!billing || reeditsLimit <= 0) return null;

    return (
      <div className={cn(
        "rounded-xl border border-primary/20 bg-primary/5",
        compact ? "p-3" : "p-4"
      )}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Wand2 className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Re-edit quota</p>
              <p className="text-xs text-muted-foreground">Only edits after a listing is created use this monthly pool.</p>
            </div>
          </div>
          <Badge variant={reeditsRemaining === 0 ? 'destructive' : 'secondary'} className="shrink-0">
            {reeditsRemaining}/{reeditsLimit} left
          </Badge>
        </div>
        {!compact && <Progress value={reeditUsagePercent} className="h-2 mt-3" />}
      </div>
    );
  };
  
  // Extended photos option - user can choose 20 photos per property (add-on)
  const [useExtendedPhotos, setUseExtendedPhotos] = useState(false);
  const [isExtendedPhotosLoading, setIsExtendedPhotosLoading] = useState(false);
  const [extendedPhotosPaymentPending, setExtendedPhotosPaymentPending] = useState(false);
  const maxPhotos = useExtendedPhotos ? EXTENDED_MAX_IMPORT_PHOTOS : DEFAULT_MAX_IMPORT_PHOTOS;
  
  // Check if user is already on a 20-photo subscription plan (no need to show add-on toggle)
  // We detect this by checking if active_listings_limit matches a 20-photo tier config
  // For now, we check if the profile has any indicator of being on 20-photo plan
  const isAlreadyOn20PhotoPlan = false; // TODO: detect from subscription tier variant when available
  // Photo analysis state
  const [isAnalyzingPhotos, setIsAnalyzingPhotos] = useState(false);
  const [photoAnalysisResult, setPhotoAnalysisResult] = useState<PhotoAnalysisResult | null>(null);
  const [hasTriedAnalysis, setHasTriedAnalysis] = useState(false);
  
  // Watermark state (simplified)
  const [customWatermark, setCustomWatermark] = useState<string | null>(null);
  const [watermarkPosition, setWatermarkPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'>('bottom-right');
  const [watermarkOpacity, setWatermarkOpacity] = useState(70);
  const watermarkInputRef = useRef<HTMLInputElement>(null);
  
  // Language selection state
  const [showMoreLanguages, setShowMoreLanguages] = useState(false);
  
  // Animation state for generating
  const [animationPhase, setAnimationPhase] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [completedPhotos, setCompletedPhotos] = useState<string[]>([]);
  const [funFacts, setFunFacts] = useState<string[]>([
    "✨ AI is analyzing lighting patterns...",
    "🎨 Color grading in progress...",
    "🏠 Making your property shine...",
    "📸 Professional magic happening...",
    "🌟 Almost there, looking great!",
  ]);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  
  // Inline edit state for import review
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  
  const featureCategories = getFeatureCategories(data.category, data.listingType);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStep]);

  // Cycle through fun facts during generation
  useEffect(() => {
    if (isGenerating) {
      const interval = setInterval(() => {
        setCurrentFactIndex(prev => (prev + 1) % funFacts.length);
        setAnimationPhase(prev => (prev + 1) % 4);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isGenerating, funFacts.length]);

  // Track completed photos
  useEffect(() => {
    if (processedCount && processedCount > 0) {
      const completedIds = photos.slice(0, processedCount).map(p => p.id);
      setCompletedPhotos(completedIds);
      setCurrentPhotoIndex(Math.min(processedCount, photos.length - 1));
    }
  }, [processedCount, photos]);

  // Initial greeting - use ref to prevent double-fire in Strict Mode
  const greetingSentRef = useRef(false);
  const pendingImportTriggerRef = useRef(false);
  useEffect(() => {
    if (!greetingSentRef.current && messages.length === 0) {
      greetingSentRef.current = true;

      // If the user pasted a URL on the homepage and was redirected here,
      // skip the welcome chit-chat and jump straight into the import step.
      const pendingUrl = consumePendingImportUrl();
      if (pendingUrl) {
        pendingImportTriggerRef.current = true;
        setImportUrl(pendingUrl);
        addBotMessage(
          "Got your link — let me grab the photos and details for you. ✨",
          'import-url'
        );
        setTimeout(() => setCurrentStep('import-url'), 400);
      } else {
        const greeting = greetings[Math.floor(Math.random() * greetings.length)];
        setTimeout(() => addBotMessage(greeting, 'welcome'), 300);
        setTimeout(() => setCurrentStep('method'), 800);
      }
    }
  }, []);

  // Once we've routed into the import step with a pre-filled URL, kick off
  // the actual import automatically. This runs exactly once.
  useEffect(() => {
    if (
      pendingImportTriggerRef.current &&
      currentStep === 'import-url' &&
      importUrl &&
      !isImporting
    ) {
      pendingImportTriggerRef.current = false;
      // Defer slightly so the UI has time to render the import step.
      const t = setTimeout(() => { handleImport(); }, 600);
      return () => clearTimeout(t);
    }
  }, [currentStep, importUrl, isImporting]);

  // Load saved template on mount
  // Template functionality removed - AI now has creative freedom

  const addBotMessage = (content: string, step: FlowStep, isQuickFlow?: boolean) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: `bot-${Date.now()}`,
        type: 'bot',
        content,
        step: 0,
        isQuickFlow
      }]);
      setIsTyping(false);
    }, 300);
  };

  const addUserMessage = (content: string) => {
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      type: 'user',
      content,
      step: 0
    }]);
  };

  const updateField = <K extends keyof PropertyFormData>(field: K, value: PropertyFormData[K]) => {
    onChange({ ...data, [field]: value });
  };

  const getPropertyTypes = () => {
    const cfg = getMarketConfig((profile as any)?.market);
    switch (data.category) {
      case 'commercial': return cfg.propertyTypes.commercial;
      case 'land': return cfg.propertyTypes.land;
      default: return cfg.propertyTypes.residential;
    }
  };

  const toggleFeature = (featureId: string) => {
    const newFeatures = data.features.includes(featureId)
      ? data.features.filter(f => f !== featureId)
      : [...data.features, featureId];
    updateField('features', newFeatures);
  };

  const toggleLanguage = (lang: DescriptionLanguage) => {
    const newLanguages = data.languages.includes(lang)
      ? data.languages.filter(l => l !== lang)
      : [...data.languages, lang];
    if (newLanguages.length > 0) updateField('languages', newLanguages);
  };

  // Watermark handlers
  const handleWatermarkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomWatermark(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeCustomWatermark = () => {
    setCustomWatermark(null);
    if (watermarkInputRef.current) {
      watermarkInputRef.current.value = '';
    }
  };

  // Handle extended photos toggle - triggers payment
  // Poll for payment confirmation from webhook
  const pollPaymentRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const stopPolling = useCallback(() => {
    if (pollPaymentRef.current) {
      clearInterval(pollPaymentRef.current);
      pollPaymentRef.current = null;
    }
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startPaymentPolling = useCallback(() => {
    stopPolling();
    let attempts = 0;
    const maxAttempts = 60; // 3 minutes (every 3s)
    
    pollPaymentRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        stopPolling();
        setExtendedPhotosPaymentPending(false);
        toast({
          title: 'Payment verification timed out',
          description: 'If you completed payment, please try toggling the option again.',
          variant: 'destructive',
        });
        return;
      }
      
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('one_off_kit_photos_remaining')
          .eq('id', user?.id || '')
          .single();
        
        if (profileData?.one_off_kit_photos_remaining === 20) {
          stopPolling();
          setUseExtendedPhotos(true);
          setExtendedPhotosPaymentPending(false);
          toast({
            title: '20-photo plan activated! 📸',
            description: 'You can now upload or import up to 20 photos for this listing.',
          });
        }
      } catch {
        // Silently retry
      }
    }, 3000);
  }, [user?.id, stopPolling]);

  const handleExtendedPhotosToggle = async (checked: boolean) => {
    if (!checked) {
      setUseExtendedPhotos(false);
      setExtendedPhotosPaymentPending(false);
      stopPolling();
      return;
    }
    
    setIsExtendedPhotosLoading(true);
    try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
            const locale = navigator.languages?.[0] || navigator.language || undefined;
            const { data, error } = await supabase.functions.invoke('create-photo-addon-checkout', {
        body: { 
          listingId: 'pending',
          preferredCurrency: checkoutCurrency,
          timezone,
          locale,
          ...getDataFastAttribution(),
        },
      });

      if (error || data?.error) {
        throw new Error(data?.error || 'Failed to start checkout');
      }

      if (data?.url) {
        window.open(data.url, '_blank');
        setExtendedPhotosPaymentPending(true);
        startPaymentPolling();
        toast({
          title: 'Complete payment in the new tab',
          description: 'Your 20-photo limit will activate automatically once payment is confirmed.',
        });
      }
    } catch (err) {
      console.error('Photo addon checkout error:', err);
      toast({
        title: 'Could not start checkout',
        description: err instanceof Error ? err.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsExtendedPhotosLoading(false);
    }
  };

  // Cinematic AI Video polling — mirrors the 20-photo add-on flow.
  const pollCinematicRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopCinematicPolling = useCallback(() => {
    if (pollCinematicRef.current) {
      clearInterval(pollCinematicRef.current);
      pollCinematicRef.current = null;
    }
  }, []);
  useEffect(() => () => stopCinematicPolling(), [stopCinematicPolling]);

  const startCinematicPolling = useCallback((baseline: number) => {
    stopCinematicPolling();
    let attempts = 0;
    const maxAttempts = 60; // 3 minutes @ 3s
    pollCinematicRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        stopCinematicPolling();
        setCinematicPaymentPending(false);
        toast({
          title: 'Payment verification timed out',
          description: 'If you completed payment, please try toggling Cinematic AI Video again.',
          variant: 'destructive',
        });
        return;
      }
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('pending_cinematic_video_credits')
          .eq('id', user?.id || '')
          .single();
        const credits = profileData?.pending_cinematic_video_credits ?? 0;
        if (credits > baseline) {
          stopCinematicPolling();
          setWantsCinematicVideo(true);
          setCinematicPaymentPending(false);
          toast({
            title: 'Cinematic AI Video ready 🎬',
            description: "You're all set — we'll generate your video right after your listing is created.",
          });
        }
      } catch {
        // Silently retry
      }
    }, 3000);
  }, [user?.id, stopCinematicPolling]);

  const handleCinematicToggle = async (checked: boolean) => {
    if (!checked) {
      setWantsCinematicVideo(false);
      setCinematicPaymentPending(false);
      stopCinematicPolling();
      return;
    }
    // Add-on is only for paying customers (a plan or the Single Property Kit).
    if (!isSubscriber) {
      toast({
        title: 'Paid plan required',
        description: 'Cinematic AI Video is an add-on for paid plans or the Single Property Kit.',
        variant: 'destructive',
      });
      return;
    }
    if (!user?.id) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to purchase the Cinematic AI Video add-on.',
        variant: 'destructive',
      });
      return;
    }
    setIsCinematicCheckoutLoading(true);
    try {
      // Read baseline so we detect the webhook increment even if the user
      // already had pending credits.
      const { data: profileData } = await supabase
        .from('profiles')
        .select('pending_cinematic_video_credits')
        .eq('id', user.id)
        .single();
      const baseline = profileData?.pending_cinematic_video_credits ?? 0;

      // If the user already has an unspent pre-paid credit (e.g. they paid
      // in a previous session but abandoned before saving), reuse it instead
      // of charging again. The credit is consumed on listing save.
      if (baseline > 0) {
        setWantsCinematicVideo(true);
        setCinematicPaymentPending(false);
        toast({
          title: 'Cinematic AI Video enabled 🎬',
          description: 'We found an unused Cinematic AI Video credit on your account — no new charge. It will be applied to this listing.',
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-cinematic-video-checkout', {
        body: {
          listingId: 'pending',
          purpose: 'initial',
          ...getDataFastAttribution(),
        },
      });
      if (error || (data as { error?: string })?.error) {
        throw new Error((data as { error?: string })?.error || 'Failed to start checkout');
      }
      const url = (data as { url?: string })?.url;
      if (url) {
        window.open(url, '_blank');
        setCinematicPaymentPending(true);
        startCinematicPolling(baseline);
        toast({
          title: 'Complete payment in the new tab',
          description: 'Cinematic AI Video will unlock automatically once payment is confirmed.',
        });
      }
    } catch (err) {
      console.error('Cinematic checkout error:', err);
      toast({
        title: 'Could not start checkout',
        description: err instanceof Error ? err.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsCinematicCheckoutLoading(false);
    }
  };

  // Advanced settings removed - AI has creative freedom

  // Step navigation
  const goToStep = (step: FlowStep, userAnswer?: string) => {
    if (userAnswer) addUserMessage(userAnswer);
    
    setTimeout(() => {
      setCurrentStep(step);
      
      // Add contextual bot messages for each step
      const stepMessages: Record<FlowStep, string> = {
        'welcome': '',
        'method': "How would you like to start? 🚀",
        'import-url': "Paste the listing URL and I'll grab everything for you! 🔗",
        'import-review': "Here's what I found! Review and adjust anything you'd like 📝",
        'category': "What are we listing today? Pick one! 🏠",
        'listing-type': `Nice! Are we ${data.category === 'land' ? 'selling or leasing' : 'selling or renting'}? 💰`,
        'property-type': "What type exactly? 🔍",
        'location': "Where's this gem located? 📍",
        'details': data.category === 'residential' 
          ? "Tell me about the space - beds, baths, size, and price! 🛏️" 
          : "What's the size and price? 📐💵",
        'furnished': "Is the property furnished? 🛋️",
        'features-quick': "Quick picks! What are the top features? ⭐",
        'features-full': "Let's get all the amenities! Pick everything that applies 🏊‍♂️",
        'highlights': "Any special notes? What makes this property unique? ✨",
        'description-settings': profile?.description_tone 
          ? "You have saved description preferences! Want to use them? ✍️"
          : "Let's set up how you want your descriptions to sound! ✍️",
        'description-customize': "Customize your description style for this listing! ✍️",
        'languages': "Almost there! What languages for your listing? 🌍",
        'photos': photos.length > 0 
          ? `You have ${photos.length} photos! Add more or let's enhance them! 📸`
          : "Now for the fun part - add your photos! 📸",
        'enhancement-style': profile?.photo_enhancement_template 
          ? "You have a saved enhancement style! Would you like to use it? 🎨"
          : "Choose your photo enhancement style! 🎨",
        'enhancement-choice': "Let's configure your photo enhancement! 🎨",
        'photo-instructions': "Any specific instructions for certain photos? Reference by number! 🎨",
        'enhance-options': "Configure virtual decoration and custom instructions 🎨",
        'media-generation': "Want a beautiful collage and video for your property? 🎬",
        'watermark-options': "Need any branding on your photos? 🏷️",
        'advanced-settings': "Review your settings 🔧",
        'final-review': "Perfect! Here's your summary. Ready to create magic? ✨",
        'generating': "Working on it... ⚡"
      };
      
      if (stepMessages[step]) {
        addBotMessage(stepMessages[step], step);
      }
    }, 300);
  };

  // Handle URL import
  const handleImport = async () => {
    if (!importUrl.trim()) return;
    
    setIsImporting(true);
    addUserMessage(`Importing: ${importUrl}`);
    
    try {
      const result = await scrapeListingFromUrl(importUrl);
      
      if (!result.success || !result.data) {
        toast({ title: "Import failed", description: result.error || "Couldn't extract data", variant: "destructive" });
        goToStep('category', "Let's do it manually instead");
        return;
      }
      
      const scraped = result.data;
      const updates: Partial<PropertyFormData> = {};
      
      // Log what we received from scraping
      console.log('Scraped data received:', {
        title: scraped.title,
        propertyType: scraped.propertyType,
        condition: scraped.condition,
        furnished: scraped.furnished,
        price: scraped.price,
        featuresCount: scraped.features?.length,
        features: scraped.features,
        hasDescription: !!scraped.description,
      });
      
      // Combine title and description for better text analysis
      const combinedText = [scraped.title, scraped.description].filter(Boolean).join(' ');
      
      if (scraped.listingType) updates.listingType = scraped.listingType;
      
      // Valid property types from TypeScript definitions
      const validResidentialTypes = ['apartment', 'house', 'villa', 'maisonette', 'townhouse', 'studio', 'penthouse'];
      const validCommercialTypes = ['office', 'shop', 'warehouse', 'restaurant', 'hotel', 'building'];
      const validLandTypes = ['residential-plot', 'commercial-plot', 'agricultural', 'industrial'];
      
      // Normalization map for property types that aren't exact matches
      const typeNormalization: Record<string, { type: PropertyFormData['type'], category: PropertyCategory }> = {
        'bungalow': { type: 'house', category: 'residential' },
        'duplex': { type: 'maisonette', category: 'residential' },
        'semi-detached': { type: 'house', category: 'residential' },
        'detached': { type: 'house', category: 'residential' },
        'flat': { type: 'apartment', category: 'residential' },
        'retail': { type: 'shop', category: 'commercial' },
        'showroom': { type: 'shop', category: 'commercial' },
        'plot': { type: 'residential-plot', category: 'land' },
        'land': { type: 'residential-plot', category: 'land' },
        'field': { type: 'agricultural', category: 'land' },
      };
      
      // Helper: detect property type from text using keyword patterns
      // Returns the most specific match found, prioritizing commercial & land over generic residential
      const detectTypeFromText = (text: string): { type: PropertyFormData['type']; category: PropertyCategory } | null => {
        const lowerText = text.toLowerCase();
        
        // Check commercial types FIRST (they're more specific and should take priority)
        const commercialPatterns: Array<{ pattern: RegExp; type: PropertyFormData['type'] }> = [
          { pattern: /\b(office|γραφείο|γραφεία|offices|office\s*space)\b/i, type: 'office' },
          { pattern: /\b(shop|κατάστημα|μαγαζί|retail|showroom|retail\s*unit|store)\b/i, type: 'shop' },
          { pattern: /\b(warehouse|αποθήκη|industrial\s*unit)\b/i, type: 'warehouse' },
          { pattern: /\b(restaurant|εστιατόριο|cafe|καφετέρια)\b/i, type: 'restaurant' },
          { pattern: /\b(hotel|ξενοδοχείο|boutique\s*hotel)\b/i, type: 'hotel' },
          { pattern: /\b(commercial\s*building|κτίριο|building\s*for|building\s*in)\b/i, type: 'building' },
          // "Building" alone in the title strongly suggests commercial
          { pattern: /(?:^|\s)(building)(?:\s|$|,|\.|!)/i, type: 'building' },
        ];
        
        // Check land types (also specific)
        const landPatterns: Array<{ pattern: RegExp; type: PropertyFormData['type'] }> = [
          { pattern: /\b(building\s*plot|residential\s*plot)\b/i, type: 'residential-plot' },
          { pattern: /\b(commercial\s*plot|commercial\s*land)\b/i, type: 'commercial-plot' },
          { pattern: /\b(agricultural|αγροτικό|αγροτεμάχιο|farm|farmland|χωράφι)\b/i, type: 'agricultural' },
          { pattern: /\b(industrial\s*land|βιομηχανικό)\b/i, type: 'industrial' },
          { pattern: /\b(plot|land|οικόπεδο)\b/i, type: 'residential-plot' },
          { pattern: /\b(field|αγρός)\b/i, type: 'agricultural' },
        ];
        
        // Check residential types (most generic, checked last)
        const residentialPatterns: Array<{ pattern: RegExp; type: PropertyFormData['type'] }> = [
          { pattern: /\b(penthouse|πεντχάουζ)\b/i, type: 'penthouse' },
          { pattern: /\b(villa|βίλα|βίλλα)\b/i, type: 'villa' },
          { pattern: /\b(maisonette|μεζονέτα|maisonnette)\b/i, type: 'maisonette' },
          { pattern: /\b(townhouse|town\s*house)\b/i, type: 'townhouse' },
          { pattern: /\b(bungalow|μπανγκαλόου)\b/i, type: 'house' },
          { pattern: /\b(duplex|ντούπλεξ)\b/i, type: 'maisonette' },
          { pattern: /\b(studio|στούντιο|γκαρσονιέρα)\b/i, type: 'studio' },
          { pattern: /\b(apartment|flat|διαμέρισμα)\b/i, type: 'apartment' },
          { pattern: /\b(house|σπίτι|κατοικία|οικία)\b/i, type: 'house' },
        ];
        
        for (const { pattern, type } of commercialPatterns) {
          if (pattern.test(lowerText)) {
            return { type, category: 'commercial' };
          }
        }
        for (const { pattern, type } of landPatterns) {
          if (pattern.test(lowerText)) {
            return { type, category: 'land' };
          }
        }
        for (const { pattern, type } of residentialPatterns) {
          if (pattern.test(lowerText)) {
            return { type, category: 'residential' };
          }
        }
        
        return null;
      };
      
      // Use scraped property type, or detect from combined text
      if (scraped.propertyType) {
        const scrapedType = scraped.propertyType.toLowerCase();
        
        // Check if it needs normalization
        if (typeNormalization[scrapedType]) {
          updates.type = typeNormalization[scrapedType].type;
          updates.category = typeNormalization[scrapedType].category;
          console.log(`Normalized property type: ${scraped.propertyType} → ${updates.type}`);
        } else if (validResidentialTypes.includes(scrapedType)) {
          updates.type = scrapedType as PropertyFormData['type'];
          updates.category = 'residential';
        } else if (validCommercialTypes.includes(scrapedType)) {
          updates.type = scrapedType as PropertyFormData['type'];
          updates.category = 'commercial';
        } else if (validLandTypes.includes(scrapedType)) {
          updates.type = scrapedType as PropertyFormData['type'];
          updates.category = 'land';
        } else {
          console.log(`Unknown property type from scraper: ${scraped.propertyType}`);
          // Default to apartment for residential
          updates.type = 'apartment';
          updates.category = 'residential';
        }
      }
      
      // CRITICAL: Cross-check the title for category mismatches
      // The scraper sometimes returns generic "apartment" for commercial buildings, land, etc.
      // The title is the most reliable indicator of the actual property type
      if (scraped.title && updates.category === 'residential') {
        const titleDetected = detectTypeFromText(scraped.title);
        if (titleDetected && titleDetected.category !== 'residential') {
          console.log(`[Import] Title override: scraper said "${updates.type}" (${updates.category}), but title suggests "${titleDetected.type}" (${titleDetected.category})`);
          updates.type = titleDetected.type;
          updates.category = titleDetected.category;
        }
      }
      
      // If no property type was found from scraping at all, try to detect from text
      if (!updates.type && combinedText) {
        const textDetected = detectTypeFromText(combinedText);
        if (textDetected) {
          updates.type = textDetected.type;
          updates.category = textDetected.category;
          console.log(`[Import] Detected type from text: ${updates.type} (${updates.category})`);
        }
      }
      
      // Apply condition if detected from scraper, or detect from text
      if (scraped.condition) {
        updates.condition = scraped.condition;
      } else if (combinedText) {
        // Fallback: detect condition from text if not returned by scraper
        const conditionPatterns = {
          'new': [/\bnew\s*build\b/i, /\bnewly\s*built\b/i, /\bbrand\s*new\b/i, /\bνεόδμητο\b/i, /\boff[\s-]?plan\b/i, /\bunder\s*construction\b/i],
          'renovated': [/\brenovated\b/i, /\brefurbished\b/i, /\bremodeled\b/i, /\bανακαινισμένο\b/i, /\bfully\s*renovated\b/i],
          'good': [/\bgood\s*condition\b/i, /\bexcellent\s*condition\b/i, /\bwell[\s-]maintained\b/i, /\bκαλή\s*κατάσταση\b/i, /\bresale\b/i, /\bsecond[\s-]?hand\b/i],
          'needs-renovation': [/\bneeds\s*renovati/i, /\bfixer\s*upper\b/i, /\bto\s*renovate\b/i, /\bχρειάζεται\s*ανακαίνιση\b/i]
        };
        for (const [condition, patterns] of Object.entries(conditionPatterns)) {
          if (patterns.some(p => p.test(combinedText))) {
            updates.condition = condition as 'new' | 'renovated' | 'good' | 'needs-renovation';
            break;
          }
        }
      }
      
      if (scraped.bedrooms !== undefined) updates.bedrooms = scraped.bedrooms;
      if (scraped.bathrooms !== undefined) updates.bathrooms = scraped.bathrooms;
      if (scraped.squareMeters) updates.squareMeters = cleanAreaSizeInput(scraped.squareMeters) || scraped.squareMeters;
      if (scraped.price) {
        // Parse price from string (AI returns formats like "$2,500" or "2,500/month")
        const priceStr = scraped.price.replace(/[€$£\s]/g, '').replace('/month', '').replace('/mo', '').trim();
        // Handle European format: $2,500 means 2500
        let parsedPrice: number;
        if (priceStr.includes('.') && priceStr.includes(',')) {
          // Mixed format - determine which is decimal separator
          const lastDot = priceStr.lastIndexOf('.');
          const lastComma = priceStr.lastIndexOf(',');
          if (lastDot > lastComma) {
            // US format: 1,234.56
            parsedPrice = parseFloat(priceStr.replace(/,/g, ''));
          } else {
            // European format: 1.234,56
            parsedPrice = parseFloat(priceStr.replace(/\./g, '').replace(',', '.'));
          }
        } else if (priceStr.includes('.') && priceStr.split('.').pop()!.length === 3) {
          // European thousand separator: 2.500 = 2500
          parsedPrice = parseInt(priceStr.replace(/\./g, ''), 10);
        } else if (priceStr.includes(',') && priceStr.split(',').pop()!.length === 3) {
          // US thousand separator: 2,500 = 2500
          parsedPrice = parseInt(priceStr.replace(/,/g, ''), 10);
        } else if (priceStr.includes(',')) {
          // Decimal comma: 2500,50 = 2500.50
          parsedPrice = parseFloat(priceStr.replace(',', '.'));
        } else if (priceStr.includes('.')) {
          // Could be decimal or thousand separator
          const afterDot = priceStr.split('.').pop()!.length;
          if (afterDot <= 2) {
            // Decimal: 2500.50
            parsedPrice = parseFloat(priceStr);
          } else {
            // Thousand separator: 2.500 = 2500
            parsedPrice = parseInt(priceStr.replace(/\./g, ''), 10);
          }
        } else {
          parsedPrice = parseInt(priceStr, 10);
        }
        
        if (!isNaN(parsedPrice) && parsedPrice > 0) {
          updates.price = String(parsedPrice);
        }
      }
      if (scraped.address) updates.address = scraped.address;
      
      // Detect city from scraped data or from text
      if (scraped.city) {
        updates.city = scraped.city;
      } else if (combinedText) {
        const lowerText = combinedText.toLowerCase();
        // Extended city matching with Greek names and common areas
        const cityPatterns = [
          { pattern: /\b(nicosia|λευκωσία|lefkosia)\b/i, city: 'Nicosia' },
          { pattern: /\b(limassol|λεμεσός|lemesos)\b/i, city: 'Limassol' },
          { pattern: /\b(larnaca|λάρνακα|larnaka)\b/i, city: 'Larnaca' },
          { pattern: /\b(paphos|pafos|πάφος)\b/i, city: 'Paphos' },
          { pattern: /\b(famagusta|αμμόχωστος|ammochostos)\b/i, city: 'Famagusta' },
          { pattern: /\b(paralimni|παραλίμνι)\b/i, city: 'Paralimni' },
          { pattern: /\b(ayia napa|agia napa|αγία νάπα)\b/i, city: 'Ayia Napa' },
          { pattern: /\b(protaras|πρωταράς)\b/i, city: 'Protaras' },
          { pattern: /\b(polis chrysochous|polis|πόλη χρυσοχούς)\b/i, city: 'Polis' },
          { pattern: /\b(strovolos|στρόβολος)\b/i, city: 'Strovolos' },
        ];
        for (const { pattern, city } of cityPatterns) {
          if (pattern.test(lowerText)) {
            updates.city = city;
            break;
          }
        }
      }
      
      // Detect features from combined title + description for better accuracy
      // Also use AI-extracted features if available
      if (combinedText || scraped.features?.length) {
        if (scraped.description) updates.highlights = scraped.description.slice(0, 1000);
        const category = updates.category || data.category || 'residential';
        const listingType = updates.listingType || data.listingType || 'sale';
        
        // Start with text-based detection
        const detected = detectFeaturesFromDescription(combinedText, category, listingType);
        console.log('Features detected from text:', detected);
        
        // Add AI-extracted features (normalized to our IDs)
        if (scraped.features && scraped.features.length > 0) {
          const normalizedAiFeatures = normalizeFeatures(scraped.features, category, listingType);
          console.log('AI features normalized:', normalizedAiFeatures);
          detected.push(...normalizedAiFeatures);
        }
        
        if (detected.length > 0) {
          updates.features = [...new Set([...data.features, ...detected])];
          console.log('Final features to preselect:', updates.features);
        }
      }
      
      // Handle furnished status from scraping
      if (scraped.furnished) {
        updates.furnished = scraped.furnished;
      }
      
      // Log final preselected values
      console.log('Import preselection summary:', {
        category: updates.category,
        type: updates.type,
        condition: updates.condition,
        furnished: updates.furnished,
        listingType: updates.listingType,
        price: updates.price,
        featuresCount: updates.features?.length,
        city: updates.city,
      });
      
      // Import photos first so we can analyze them
      let importedPhotos: PropertyPhoto[] = [];
      console.log('=== Photo Import Starting ===');
      console.log('Scraped images count:', scraped.images?.length || 0);
      console.log('First 3 image URLs:', scraped.images?.slice(0, 3) || []);
      
      if (scraped.images?.length) {
        const importCount = Math.min(scraped.images.length, maxPhotos);
        toast({ title: "Downloading photos...", description: `Importing ${importCount} photo${importCount !== 1 ? 's' : ''}` });
        const downloadResult = await downloadImagesAsFiles(scraped.images, importUrl, maxPhotos);
        console.log('Download result:', { files: downloadResult.files.length, totalFound: downloadResult.totalFound });
        if (downloadResult.files.length > 0) {
          importedPhotos = downloadResult.files.map((img, idx) => ({
            id: `imported-${Date.now()}-${idx}`,
            file: img.file,
            preview: URL.createObjectURL(img.file),
          }));
          onPhotosChange(importedPhotos);
        } else {
          console.log('No files were downloaded!');
        }
      } else {
        console.log('No images in scraped data!');
      }
      
      // Run photo analysis during import to auto-detect property type, condition, and features
      if (importedPhotos.length > 0) {
        try {
          setIsAnalyzingPhotos(true);
          toast({
            title: "🔍 Analyzing photos...",
            description: "AI is detecting property type, condition, and features",
          });
          
          // Convert photos to base64 for analysis (max 5 photos)
          const imagePromises = importedPhotos.slice(0, 5).map(async (photo) => {
            if (!photo.file) return null;
            return new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(photo.file);
            });
          });
          
          const imageBase64Array = (await Promise.all(imagePromises)).filter((img): img is string => img !== null);
          
          if (imageBase64Array.length > 0) {
            const analysisResult = await analyzePhotos(imageBase64Array);
            setPhotoAnalysisResult(analysisResult);
            setHasTriedAnalysis(true); // Mark as done so it won't re-run on photos step
            
            // Apply analysis results - but prefer scraped data if available
            if (analysisResult.confidence >= 0.5) {
              // Auto-set category if not already set from scraping
              if (!updates.category && analysisResult.suggestedCategory) {
                updates.category = analysisResult.suggestedCategory;
              }
              
              // Auto-set property type if not already set from scraping
              if (!updates.type && analysisResult.suggestedPropertyType) {
                updates.type = analysisResult.suggestedPropertyType as ResidentialType | CommercialType | LandType;
              }
              
              // Auto-set condition if not already set from scraping
              if (!updates.condition && analysisResult.suggestedCondition) {
                updates.condition = analysisResult.suggestedCondition as 'new' | 'renovated' | 'good' | 'needs-renovation';
              }
              
              // Merge detected features from photos with scraped features
              if (analysisResult.visibleFeatures.length > 0) {
                const normalizedFeatures = normalizeAnalysisFeatures(analysisResult.visibleFeatures);
                const existingFeatures = updates.features || data.features || [];
                updates.features = [...new Set([...existingFeatures, ...normalizedFeatures])];
              }
              
              const detectedItems = [];
              if (!data.type && updates.type) detectedItems.push(`Type: ${updates.type}`);
              if (!data.condition && updates.condition) detectedItems.push(`Condition: ${updates.condition}`);
              if (analysisResult.visibleFeatures.length > 0) {
                detectedItems.push(`${analysisResult.visibleFeatures.length} features from photos`);
              }
              
              if (detectedItems.length > 0) {
                console.log('Photo analysis detected:', detectedItems.join(', '));
              }
            }
          }
        } catch (error) {
          console.error('Photo analysis during import failed:', error);
          // Silent failure - user can still confirm/adjust manually
        } finally {
          setIsAnalyzingPhotos(false);
        }
      }
      
      onChange({ ...data, ...updates });
      
      toast({ title: "Import successful! 🎉", description: "Review your details below" });
      goToStep('import-review');
    } catch (error) {
      console.error('Import error:', error);
      toast({ title: "Import failed", variant: "destructive" });
      goToStep('category');
    } finally {
      setIsImporting(false);
      setImportUrl('');
    }
  };

  // Handle photo upload
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;
    const availableSlots = maxPhotos - photos.length;
    if (availableSlots <= 0) return;
    
    const filesToAdd = Array.from(files).slice(0, availableSlots);
    const newPhotos: PropertyPhoto[] = filesToAdd.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
    }));
    
    onPhotosChange([...photos, ...newPhotos]);
  }, [photos, onPhotosChange]);

  const removePhoto = (id: string) => {
    const photo = photos.find(p => p.id === id);
    if (photo) URL.revokeObjectURL(photo.preview);
    onPhotosChange(photos.filter(p => p.id !== id));
  };

  // Handle final generation
  const handleGenerate = () => {
    const photosWithOptions = photos.map(photo => {
      const isFullStaging = fullStagingPhotoIds.has(photo.id);
      return ({
      ...photo,
      enhancementOptions: {
        ...enhancementOptions,
        // Per-photo staging & golden hour overrides. Full Staging (empty
        // room → furnished) is mutually exclusive with Virtual Decoration
        // (accents like towels/pillows) on the same photo — decorating an
        // empty room produces weird results (towels on the floor).
        addDecorations: !isFullStaging && stagingPhotoIds.has(photo.id),
        goldenHourMode: goldenHourPhotoIds.has(photo.id),
        fullStaging: isFullStaging,
        fullStagingStyle: fullStagingStyles[photo.id] || fullStagingGlobalStyle,
        fullStagingRoomType: isFullStaging
          ? (fullStagingRoomTypes[photo.id] || 'living')
          : undefined,
        // Use per-photo prompt if enabled, otherwise use single prompt
        customPrompt: perPhotoPromptsEnabled 
          ? (perPhotoPrompts[photo.id]?.trim() || undefined)
          : (customPrompt.trim() || undefined),
        customWatermark: customWatermark || undefined,
        watermarkPosition: customWatermark ? watermarkPosition : undefined,
        watermarkOpacity: customWatermark ? watermarkOpacity : undefined,
      },
    });
    });
    onPhotosChange(photosWithOptions);
    // If the user opted into Cinematic AI Video, stash a flag so the parent
    // page can launch the Stripe checkout as soon as the listing is saved.
    try {
      if (wantsCinematicVideo) {
        sessionStorage.setItem('pendingCinematicPurchase', '1');
      } else {
        sessionStorage.removeItem('pendingCinematicPurchase');
      }
    } catch { /* sessionStorage unavailable */ }
    onGenerate(photosWithOptions, processInBackground);
  };

  // Analyze photos to auto-detect property attributes
  const runPhotoAnalysis = useCallback(async () => {
    if (photos.length === 0 || hasTriedAnalysis || isAnalyzingPhotos) return;
    
    // Only run analysis if property type or condition is not already set
    const needsAnalysis = !data.type || !data.condition || data.features.length === 0;
    if (!needsAnalysis) return;
    
    setIsAnalyzingPhotos(true);
    setHasTriedAnalysis(true);
    
    try {
      // Convert photos to base64 for analysis
      const imagePromises = photos.slice(0, 5).map(async (photo) => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(photo.file);
        });
      });
      
      const imageBase64Array = await Promise.all(imagePromises);
      
      toast({
        title: "🔍 Analyzing photos...",
        description: "AI is detecting property type, condition, and features",
      });
      
      const result = await analyzePhotos(imageBase64Array);
      setPhotoAnalysisResult(result);
      
      // Auto-apply suggestions if confidence is good
      if (result.confidence >= 0.5) {
        const updates: Partial<PropertyFormData> = {};
        
        // Auto-set category if not set
        if (!data.category || data.category === 'residential') {
          updates.category = result.suggestedCategory;
        }
        
        // Auto-set property type if not set
        if (!data.type && result.suggestedPropertyType) {
          updates.type = result.suggestedPropertyType as ResidentialType | CommercialType | LandType;
        }
        
        // Auto-set condition if not set
        if (!data.condition && result.suggestedCondition) {
          updates.condition = result.suggestedCondition as 'new' | 'renovated' | 'good' | 'needs-renovation';
        }
        
        // Merge detected features
        if (result.visibleFeatures.length > 0) {
          const normalizedFeatures = normalizeAnalysisFeatures(result.visibleFeatures);
          updates.features = [...new Set([...data.features, ...normalizedFeatures])];
        }
        
        // Auto-set price suggestion if not set and we have a suggestion
        if (!data.price && result.priceRangeSuggestion) {
          const suggestedPrice = Math.round((result.priceRangeSuggestion.min + result.priceRangeSuggestion.max) / 2);
          updates.price = suggestedPrice.toString();
        }
        
        if (Object.keys(updates).length > 0) {
          onChange({ ...data, ...updates });
          
          const detectedItems = [];
          if (updates.type) detectedItems.push(`Property type: ${updates.type}`);
          if (updates.condition) detectedItems.push(`Condition: ${updates.condition}`);
          if (updates.features && updates.features.length > data.features.length) {
            detectedItems.push(`${updates.features.length - data.features.length} features detected`);
          }
          if (updates.price) detectedItems.push(`Suggested price: ${parseInt(updates.price).toLocaleString()}`);
          
          toast({
            title: "✨ AI Analysis Complete!",
            description: detectedItems.join(' • ') || "Photo analysis finished",
          });
        }
      } else {
        toast({
          title: "Photo analysis done",
          description: "Could not confidently detect property details. Please fill in manually.",
        });
      }
    } catch (error) {
      console.error('Photo analysis error:', error);
      // Silent failure - user can still fill in manually
    } finally {
      setIsAnalyzingPhotos(false);
    }
  }, [photos, data, onChange, hasTriedAnalysis, isAnalyzingPhotos]);

  // Trigger photo analysis when photos are uploaded and we're on the photos step
  useEffect(() => {
    if (photos.length > 0 && currentStep === 'photos' && !hasTriedAnalysis) {
      // Small delay to let the UI settle
      const timer = setTimeout(() => {
        runPhotoAnalysis();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [photos.length, currentStep, hasTriedAnalysis, runPhotoAnalysis]);

  // Check if we can proceed
  const canProceedFromDetails = data.price && (data.category !== 'residential' || true);
  const canProceedFromLocation = data.city;

  // Render generating state - Interactive and fun!
  if (isGenerating) {
    const currentPhoto = photos[currentPhotoIndex] || photos[0];
    const progress = processedCount ? (processedCount / photos.length) * 100 : 0;
    
    const enhancementSteps = [
      { icon: Sun, label: 'Lighting' },
      { icon: Palette, label: 'Colors' },
      { icon: Sparkles, label: 'Clarity' },
      { icon: ScanSearch, label: 'Details' },
    ];
    
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-2xl flex flex-col items-center gap-6">

          {/* Header */}
          <div className="text-center space-y-1.5">
            <h2 className="text-2xl sm:text-3xl font-display font-semibold text-foreground tracking-tight">
              Creating Your Listing
            </h2>
            <p className="text-sm text-muted-foreground">
              {generationPhase === 'uploading-photos' ? (
                'Saving photos to cloud storage…'
              ) : generationPhase === 'generating-media' ? (
                <>
                  {mediaGenerationStatus.profilePicture === 'generating' && 'Creating PDF flyer…'}
                  {mediaGenerationStatus.video === 'generating' && 'Generating video…'}
                  {mediaGenerationStatus.profilePicture === 'complete' && mediaGenerationStatus.video === 'generating' && 'PDF flyer done, video in progress…'}
                  {mediaGenerationStatus.profilePicture === 'generating' && mediaGenerationStatus.video === 'complete' && 'Video done, PDF flyer in progress…'}
                </>
              ) : generationPhase === 'generating-description' ? (
                'Writing your property description…'
              ) : (
                funFacts[currentFactIndex]
              )}
            </p>
          </div>

          {/* Main Photo */}
          {currentPhoto && (
            <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-muted">
              <img
                src={currentPhoto.enhanced || currentPhoto.preview}
                alt={`Property photo ${currentPhotoIndex + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Subtle shimmer sweep */}
              {generationPhase === 'enhancing' && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  <div className="gen-shimmer absolute inset-0" />
                </div>
              )}

              {/* Photo counter pill */}
                <div className="absolute top-3 left-3 bg-primary/80 backdrop-blur-sm text-primary-foreground text-xs font-medium px-2.5 py-1 rounded-full">
                  {currentPhotoIndex + 1} / {photos.length}
                </div>

              {/* Enhancement step indicators */}
              {generationPhase === 'enhancing' && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2">
                  {enhancementSteps.map((step, i) => {
                    const StepIcon = step.icon;
                    const isActive = i === animationPhase;
                    return (
                      <div
                        key={step.label}
                        className={cn(
                          "flex items-center gap-1 sm:gap-1.5 px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium transition-all duration-500 backdrop-blur-sm",
                          isActive
                            ? "bg-primary text-primary-foreground scale-105"
                            : "bg-primary/30 text-primary-foreground/70"
                        )}
                      >
                        <StepIcon className="w-3 h-3" />
                        <span className="hidden sm:inline">{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Description overlay */}
              {generationPhase === 'generating-description' && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-foreground">Generating description…</p>
                  <div className="flex gap-1">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Thumbnail strip */}
          <div className="flex gap-1.5 overflow-x-auto pb-0.5 justify-center w-full">
            {photos.map((photo, index) => {
              const isCompleted = completedPhotos.includes(photo.id);
              const isCurrent = processingPhotoId === photo.id;
              
              return (
                <div
                  key={photo.id}
                  className={cn(
                    "relative flex-shrink-0 w-10 h-10 rounded-md overflow-hidden transition-all duration-300",
                    isCurrent
                      ? "ring-2 ring-primary ring-offset-1 ring-offset-background"
                      : isCompleted
                      ? "opacity-80"
                      : "opacity-35"
                  )}
                >
                  <img src={photo.preview} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  {isCompleted && (
                    <div className="absolute inset-0 bg-primary/40 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-primary-foreground" />
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-3.5 h-3.5 text-background animate-spin drop-shadow" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress */}
          <div className="w-full space-y-3">
            <div className="w-full h-1 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
                style={{ width: `${generationPhase === 'generating-media' ? 100 : Math.max(progress, 2)}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {generationPhase === 'uploading-photos'
                  ? 'Saving photos…'
                  : generationPhase === 'generating-description'
                  ? 'Writing descriptions…'
                  : generationPhase === 'generating-media'
                  ? 'Generating media…'
                  : `${processedCount || 0} of ${photos.length} photos enhanced`
                }
              </span>
              {generationPhase === 'enhancing' && onStopEnhancement && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onStopEnhancement();
                  }}
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted"
                >
                  <Square className="w-3 h-3" />
                  <span>Stop</span>
                </button>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground/70">
            <MonitorSmartphone className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Please keep this window open</span>
          </div>
        </div>

        {/* CSS for shimmer */}
        <style>{`
          .gen-shimmer {
            background: linear-gradient(
              105deg,
              transparent 40%,
              rgba(255,255,255,0.08) 45%,
              rgba(255,255,255,0.15) 50%,
              rgba(255,255,255,0.08) 55%,
              transparent 60%
            );
            background-size: 200% 100%;
            animation: genShimmer 3s ease-in-out infinite;
          }
          @keyframes genShimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  // Usage stats compact display component
  const UsageStatsBar = () => {
    if (!billing || !tierConfig) return null;
    
    const isLowOnProperties = propertiesRemaining !== 'unlimited' && (propertiesRemaining as number) <= 1;
    
    return (
      <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-7 h-7 rounded-lg flex items-center justify-center",
              billing.subscriptionTier === 'studio' ? "bg-amber-100 dark:bg-amber-900/30" :
              billing.subscriptionTier === 'growth' ? "bg-purple-100 dark:bg-purple-900/30" :
              "bg-blue-100 dark:bg-blue-900/30"
            )}>
              <TierIcon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-medium">{tierConfig.displayName}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className={cn("flex items-center gap-1", isLowOnProperties && "text-amber-600")}>
                  <Home className="w-3 h-3" />
                  {propertiesRemaining === 'unlimited' ? '∞' : propertiesRemaining} properties
                </span>
                {reeditsLimit > 0 && (
                  <span className="flex items-center gap-1">
                    <Wand2 className="w-3 h-3" />
                    {reeditsRemaining}/{reeditsLimit} re-edits
                  </span>
                )}
              </div>
            </div>
          </div>
          {billing.subscriptionTier !== 'studio' && (
            <Button 
              size="sm" 
              variant="outline"
              className="h-7 text-xs"
              onClick={() => navigate('/pricing')}
            >
              Upgrade
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Render step content
  const renderStepContent = () => {
    // Pre-compute values for case blocks that need them
    const topFeatures = featureCategories.flatMap(cat => cat.features.slice(0, 4)).slice(0, 12);
    const isMaxLanguages = data.languages.length >= 4;
    const hasSelectedMoreLanguage = data.languages.some(l => 
      moreLanguages.some(ml => ml.value === l)
    );
    const shouldShowMoreLang = showMoreLanguages || hasSelectedMoreLanguage;
    const canAddMorePhotos = photos.length < maxPhotos;
    const renderLanguageButton = (lang: typeof popularLanguages[0]) => {
      const isSelected = data.languages.includes(lang.value);
      const isDisabled = (isMaxLanguages && !isSelected) || (data.languages.length === 1 && isSelected);
      
      return (
        <button
          key={lang.value}
          onClick={() => !isDisabled && toggleLanguage(lang.value)}
          disabled={isDisabled}
          className={cn(
            "flex items-center gap-2 p-3 sm:p-2.5 rounded-lg border-2 transition-all min-h-[44px]",
            isSelected 
              ? 'border-primary bg-primary/10 shadow-sm' 
              : 'border-border hover:border-primary/50',
            isDisabled && !isSelected && 'opacity-40 cursor-not-allowed'
          )}
        >
          <span className="text-lg">{lang.flag}</span>
          <span className="font-medium text-sm flex-1 text-left">{lang.label}</span>
          {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
        </button>
      );
    };

    switch (currentStep) {
      case 'method':
        return (
          <div className="space-y-4 animate-scale-in">
            {/* Usage stats banner */}
            <UsageStatsBar />
            <ReeditQuotaBanner compact />
            
            {/* Check if user can create a listing */}
            {billing && !canCreateNewListing && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      Great job on your first listing! 🎉
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      You've used your free trial property. Subscribe to create more listings with full-quality photos and copyable descriptions.
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <Button 
                        size="sm" 
                        className="h-8 text-xs gap-1.5"
                        onClick={() => navigate('/pricing')}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Subscribe Now
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-8 text-xs"
                        onClick={() => navigate('/my-listings')}
                      >
                        View My Listings
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => canCreateNewListing ? goToStep('category', "Start fresh") : navigate('/pricing')}
                disabled={billing && !canCreateNewListing}
                className={cn(
                  "flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-300 group min-h-[120px]",
                  billing && !canCreateNewListing 
                    ? "border-muted bg-muted/50 cursor-not-allowed opacity-60"
                    : "border-border hover:border-primary hover:bg-primary/5 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5 active:scale-[0.98]"
                )}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300">
                  <Wand2 className="w-6 h-6 text-primary group-hover:animate-bounce-subtle" />
                </div>
                <div className="text-center">
                  <p className="font-medium">Start Fresh</p>
                  <p className="text-xs text-muted-foreground">Build from scratch</p>
                </div>
              </button>
              <button
                onClick={() => canCreateNewListing ? goToStep('import-url', "Import from URL") : navigate('/pricing')}
                disabled={billing && !canCreateNewListing}
                className={cn(
                  "flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-300 group min-h-[120px]",
                  billing && !canCreateNewListing 
                    ? "border-muted bg-muted/50 cursor-not-allowed opacity-60"
                    : "border-border hover:border-primary hover:bg-primary/5 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5 active:scale-[0.98]"
                )}
                style={{ animationDelay: '0.1s' }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300">
                  <Link className="w-6 h-6 text-primary group-hover:animate-bounce-subtle" />
                </div>
                <div className="text-center">
                  <p className="font-medium">Import URL</p>
                  <p className="text-xs text-muted-foreground">Auto-fill from link</p>
                </div>
              </button>
            </div>
          </div>
        );

      case 'import-url':
        return (
          <div className="space-y-3 animate-fade-up">
            {isImporting && (
              <div className="p-4 rounded-xl bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/20 animate-pulse">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary animate-spin" style={{ animationDuration: '3s' }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Grabbing your listing...</p>
                    <p className="text-xs text-muted-foreground">This might take a minute, but you're saving hours! ⏰</p>
                  </div>
                </div>
                <div className="text-xs text-center text-muted-foreground mt-3 italic">
                  💡 Tip: Sit back and relax — we're doing the heavy lifting so you don't have to!
                </div>
              </div>
            )}

            {/* Extended photos toggle - choose before importing */}
            {!isImporting && !isAlreadyOn20PhotoPlan && (
              <div className={cn(
                "p-3 rounded-xl border transition-all",
                useExtendedPhotos 
                  ? "border-primary/40 bg-primary/5" 
                  : extendedPhotosPaymentPending
                    ? "border-amber-400/40 bg-amber-50 dark:bg-amber-950/20"
                    : "border-border/50 bg-card/50"
              )}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <ImageIcon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Import up to 20 photos</p>
                      <p className="text-xs text-muted-foreground">
                        Default is 10 · One-time $5 add-on per listing
                      </p>
                    </div>
                  </div>
                  {!extendedPhotosPaymentPending && !useExtendedPhotos && (
                    <Switch
                      checked={false}
                      onCheckedChange={handleExtendedPhotosToggle}
                      disabled={isExtendedPhotosLoading}
                    />
                  )}
                  {useExtendedPhotos && (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      <Check className="w-3 h-3 mr-1" /> Active
                    </Badge>
                  )}
                </div>
                {isExtendedPhotosLoading && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Opening payment...</span>
                  </div>
                )}
                {extendedPhotosPaymentPending && !useExtendedPhotos && (
                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 flex-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Waiting for payment confirmation...</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        setExtendedPhotosPaymentPending(false);
                        stopPolling();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="https://example.com/property-listing..."
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                className="pl-10"
                onKeyDown={(e) => e.key === 'Enter' && handleImport()}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('method')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={handleImport} disabled={!importUrl.trim() || isImporting} className="flex-1">
                {isImporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <>Import <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
        );

      case 'import-review':
        // Show imported data summary and allow user to continue or adjust
        {
          const _mcfg = getMarketConfig((profile as any)?.market);
          const _currency = ((profile as any)?.currency as string) || _mcfg.currency;
          const _areaUnit = (((profile as any)?.area_unit as string) || _mcfg.areaUnit) as 'sqft' | 'sqm';
          const _sym = ({ USD:'$', EUR:'€', GBP:'£', AED:'AED ', AUD:'A$', CAD:'C$', NZD:'NZ$', CHF:'CHF ', SAR:'SAR ', QAR:'QAR ', KWD:'KWD ', BHD:'BHD ', OMR:'OMR ', JPY:'¥', SGD:'S$', HKD:'HK$', INR:'₹' } as Record<string,string>)[_currency] || `${_currency} `;
          const _areaLabel = _areaUnit === 'sqft' ? 'sqft' : 'm²';
        return (
          <div className="space-y-4 animate-scale-in">
            <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 animate-fade-up">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300 font-medium mb-1">
                <Check className="w-4 h-4" />
                Import successful!
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                Please review and verify the details below — tap any field to edit if something doesn't look right.
              </p>
            </div>
            
            {/* Show imported data summary with inline edit */}
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                {/* Listing Type */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'listingType') {
                      setEditingField('listingType');
                      setEditValue(data.listingType);
                    }
                  }}
                >
                  {editingField === 'listingType' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">Listing Type</span>
                      <Select 
                        value={editValue} 
                        onValueChange={(val) => {
                          onChange({ ...data, listingType: val as 'sale' | 'rent' });
                          setEditingField(null);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sale">For Sale</SelectItem>
                          <SelectItem value="rent">For Rent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">Listing Type</span>
                      <p className="font-medium capitalize flex items-center justify-between">
                        {data.listingType === 'rent' ? 'For Rent' : 'For Sale'}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* Property Type */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'type') {
                      setEditingField('type');
                      setEditValue(data.type || '');
                    }
                  }}
                >
                  {editingField === 'type' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">Property Type</span>
                      <Select 
                        value={editValue} 
                        onValueChange={(val) => {
                          onChange({ ...data, type: val as ResidentialType | CommercialType | LandType });
                          setEditingField(null);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {data.category === 'residential' && residentialTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.emoji} {t.label}</SelectItem>
                          ))}
                          {data.category === 'commercial' && commercialTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.emoji} {t.label}</SelectItem>
                          ))}
                          {data.category === 'land' && landTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.emoji} {t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">Property Type</span>
                      <p className="font-medium capitalize flex items-center justify-between">
                        {data.type || 'Not set'}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* City */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'city') {
                      setEditingField('city');
                      setEditValue(data.city);
                    }
                  }}
                >
                  {editingField === 'city' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">City</span>
                      <CityAutocomplete
                        value={editValue}
                        onChange={(val) => {
                          setEditValue(val);
                          onChange({ ...data, city: val });
                        }}
                        compact
                        autoFocus
                        fallbackCities={popularCities}
                      />
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">City</span>
                      <p className="font-medium flex items-center justify-between">
                        {data.city || 'Not set'}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* Price */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'price') {
                      setEditingField('price');
                      setEditValue(data.price);
                    }
                  }}
                >
                  {editingField === 'price' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">{data.listingType === 'rent' ? 'Monthly Rent' : 'Price'}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs">{_sym.trim()}</span>
                        <Input 
                          type="number"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onBlur={() => {
                            onChange({ ...data, price: editValue });
                            setEditingField(null);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              onChange({ ...data, price: editValue });
                              setEditingField(null);
                            }
                          }}
                          className="h-7 text-xs"
                          autoFocus
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">{data.listingType === 'rent' ? 'Monthly Rent' : 'Price'}</span>
                      <p className="font-medium flex items-center justify-between">
                        {data.price ? `${_sym}${parseInt(data.price).toLocaleString()}${data.listingType === 'rent' ? '/mo' : ''}` : 'Not set'}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* Bedrooms */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'bedrooms') {
                      setEditingField('bedrooms');
                      setEditValue(String(data.bedrooms));
                    }
                  }}
                >
                  {editingField === 'bedrooms' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">Bedrooms</span>
                      <Input 
                        type="number"
                        min={0}
                        max={20}
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onBlur={() => {
                          onChange({ ...data, bedrooms: parseInt(editValue) || 0 });
                          setEditingField(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            onChange({ ...data, bedrooms: parseInt(editValue) || 0 });
                            setEditingField(null);
                          }
                        }}
                        className="h-7 text-xs"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">Bedrooms</span>
                      <p className="font-medium flex items-center justify-between">
                        {data.bedrooms}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* Bathrooms */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'bathrooms') {
                      setEditingField('bathrooms');
                      setEditValue(String(data.bathrooms));
                    }
                  }}
                >
                  {editingField === 'bathrooms' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">Bathrooms</span>
                      <Input 
                        type="number"
                        min={0}
                        max={20}
                        step={0.5}
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onBlur={() => {
                          onChange({ ...data, bathrooms: parseFloat(editValue) || 0 });
                          setEditingField(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            onChange({ ...data, bathrooms: parseFloat(editValue) || 0 });
                            setEditingField(null);
                          }
                        }}
                        className="h-7 text-xs"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">Bathrooms</span>
                      <p className="font-medium flex items-center justify-between">
                        {data.bathrooms}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* Area Size */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'squareMeters') {
                      setEditingField('squareMeters');
                      setEditValue(sqmToDisplayArea(data.squareMeters, _areaUnit));
                    }
                  }}
                >
                  {editingField === 'squareMeters' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">Size ({_areaLabel})</span>
                      <Input 
                        type="text"
                        inputMode="decimal"
                        value={editValue}
                        onChange={e => setEditValue(cleanAreaSizeInput(e.target.value))}
                        onBlur={() => {
                          onChange({ ...data, squareMeters: displayAreaToSqm(editValue, _areaUnit) });
                          setEditingField(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            onChange({ ...data, squareMeters: displayAreaToSqm(editValue, _areaUnit) });
                            setEditingField(null);
                          }
                        }}
                        className="h-7 text-xs"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">Size</span>
                      <p className="font-medium flex items-center justify-between">
                        {data.squareMeters ? `${sqmToDisplayArea(data.squareMeters, _areaUnit) || data.squareMeters} ${_areaLabel}` : 'Not set'}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* Furnished */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'furnished') {
                      setEditingField('furnished');
                      setEditValue(data.furnished || '');
                    }
                  }}
                >
                  {editingField === 'furnished' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">Furnished</span>
                      <Select 
                        value={editValue} 
                        onValueChange={(val) => {
                          onChange({ ...data, furnished: val as FurnishedType });
                          setEditingField(null);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fully">Fully Furnished</SelectItem>
                          <SelectItem value="partially">Partially</SelectItem>
                          <SelectItem value="unfurnished">Unfurnished</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">Furnished</span>
                      <p className="font-medium capitalize flex items-center justify-between">
                        {data.furnished === 'fully' ? 'Fully Furnished' : data.furnished === 'partially' ? 'Partially' : data.furnished === 'unfurnished' ? 'Unfurnished' : 'Not set'}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>

                {/* Condition */}
                <div 
                  className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group relative"
                  onClick={() => {
                    if (editingField !== 'condition') {
                      setEditingField('condition');
                      setEditValue(data.condition || '');
                    }
                  }}
                >
                  {editingField === 'condition' ? (
                    <div className="space-y-1" onClick={e => e.stopPropagation()}>
                      <span className="text-muted-foreground text-xs">Condition</span>
                      <Select 
                        value={editValue} 
                        onValueChange={(val) => {
                          onChange({ ...data, condition: val as 'new' | 'renovated' | 'good' | 'needs-renovation' });
                          setEditingField(null);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {conditionOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.emoji} {opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <span className="text-muted-foreground text-xs">Condition</span>
                      <p className="font-medium capitalize flex items-center justify-between">
                        {data.condition ? data.condition.replace('-', ' ') : 'Not set'}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Features - inline editing */}
              <div 
                className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group"
                onClick={() => {
                  if (editingField !== 'features') {
                    setEditingField('features');
                  }
                }}
              >
                {editingField === 'features' ? (
                  <div className="space-y-2" onClick={e => e.stopPropagation()}>
                    <span className="text-muted-foreground text-xs flex items-center justify-between">
                      Features ({data.features.length})
                      <Button 
                        size="sm" 
                        className="h-5 text-xs px-2"
                        onClick={() => setEditingField(null)}
                      >
                        Done
                      </Button>
                    </span>
                    <div className="max-h-[200px] overflow-y-auto space-y-2">
                      {featureCategories.map(category => (
                        <div key={category.id} className="space-y-1">
                          <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                            {category.label}
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {category.features.map(feature => {
                              const Icon = feature.icon;
                              const selected = data.features.includes(feature.id);
                              return (
                                <button
                                  key={feature.id}
                                  onClick={() => toggleFeature(feature.id)}
                                  className={cn(
                                    "flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-all border min-h-[28px]",
                                    selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/50 bg-background'
                                  )}
                                >
                                  <Icon className="w-2.5 h-2.5" />
                                  {feature.label}
                                  {selected && <Check className="w-2.5 h-2.5" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="text-muted-foreground text-xs flex items-center justify-between">
                      Features ({data.features.length})
                      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                    </span>
                    <p className="font-medium text-xs mt-1 flex flex-wrap gap-1">
                      {data.features.slice(0, 8).map(f => (
                        <span key={f} className="px-1.5 py-0.5 bg-primary/10 rounded text-primary">{f.replace(/-/g, ' ')}</span>
                      ))}
                      {data.features.length > 8 && <span className="text-muted-foreground">+{data.features.length - 8} more</span>}
                      {data.features.length === 0 && <span className="text-muted-foreground">Tap to add features</span>}
                    </p>
                  </>
                )}
              </div>

              {/* Description */}
              <div 
                className="p-2 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors group"
                onClick={() => {
                  if (editingField !== 'highlights') {
                    setEditingField('highlights');
                    setEditValue(data.highlights || '');
                  }
                }}
              >
                {editingField === 'highlights' ? (
                  <div className="space-y-1" onClick={e => e.stopPropagation()}>
                    <span className="text-muted-foreground text-xs">Description</span>
                    <Textarea 
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => {
                        onChange({ ...data, highlights: editValue });
                        setEditingField(null);
                      }}
                      className="text-xs min-h-[80px]"
                      autoFocus
                    />
                    <Button 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => {
                        onChange({ ...data, highlights: editValue });
                        setEditingField(null);
                      }}
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <>
                    <span className="text-muted-foreground text-xs flex items-center justify-between">
                      Description
                      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                    </span>
                    <p className="font-medium text-xs mt-1 line-clamp-2">
                      {data.highlights || <span className="text-muted-foreground">Tap to add description</span>}
                    </p>
                  </>
                )}
              </div>
            </div>
            
            <Button 
              onClick={() => {
                setEditingField(null);
                goToStep('description-settings', 'Ready for settings');
              }}
              className="w-full transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 active:scale-[0.98]"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
        }
      case 'category':
        return (
          <div className="space-y-4 animate-scale-in">
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'residential', icon: Home, label: 'Residential', emoji: '🏠' },
                { value: 'commercial', icon: Building2, label: 'Commercial', emoji: '🏢' },
                { value: 'land', icon: MapPin, label: 'Land', emoji: '🌳' },
              ].map((cat, idx) => (
                <button
                  key={cat.value}
                  onClick={() => {
                    // Only reset type/features if changing category
                    if (data.category !== cat.value) {
                      onChange({ ...data, category: cat.value as PropertyCategory, type: '', features: [] });
                    }
                    goToStep('listing-type', cat.label);
                  }}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:scale-[0.97] opacity-0 animate-fade-up",
                    data.category === cat.value 
                      ? 'border-primary bg-primary/10 shadow-md shadow-primary/20' 
                      : 'border-border hover:border-primary/50 hover:bg-primary/5'
                  )}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <span className="text-2xl transition-transform duration-300 group-hover:scale-110">{cat.emoji}</span>
                  <span className="font-medium text-sm">{cat.label}</span>
                </button>
              ))}
            </div>
            <Button 
              variant="outline"
              onClick={() => goToStep('method')}
              className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        );

      case 'listing-type':
        return (
          <div className="space-y-4 animate-scale-in">
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'sale', label: 'For Sale', emoji: '🏷️' },
                { value: 'rent', label: data.category === 'land' ? 'For Lease' : 'For Rent', emoji: '🔑' },
              ].map((type, idx) => (
                <button
                  key={type.value}
                  onClick={() => {
                    updateField('listingType', type.value as 'sale' | 'rent');
                    goToStep('property-type', type.label);
                  }}
                  className={cn(
                    "flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:scale-[0.97] opacity-0 animate-fade-up",
                    data.listingType === type.value 
                      ? 'border-primary bg-primary/10 shadow-md shadow-primary/20' 
                      : 'border-border hover:border-primary/50 hover:bg-primary/5'
                  )}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <span className="text-xl">{type.emoji}</span>
                  <span className="font-medium">{type.label}</span>
                </button>
              ))}
            </div>
            <Button 
              variant="outline"
              onClick={() => goToStep('category')}
              className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        );

      case 'property-type':
        return (
          <div className="space-y-3 animate-scale-in">
            <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto">
              {getPropertyTypes().map((type, idx) => (
                <button
                  key={type.value}
                  onClick={() => {
                    updateField('type', type.value as PropertyFormData['type']);
                    goToStep('location', type.label);
                  }}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-xl border transition-all duration-300 text-sm min-h-[44px] hover:-translate-y-0.5 hover:shadow-md active:scale-[0.97] opacity-0 animate-fade-up",
                    data.type === type.value 
                      ? 'border-primary bg-primary/10 shadow-sm shadow-primary/20' 
                      : 'border-border hover:border-primary/50 hover:bg-primary/5'
                  )}
                  style={{ animationDelay: `${Math.min(idx * 0.05, 0.3)}s` }}
                >
                  <span>{type.emoji}</span>
                  <span className="font-medium">{type.label}</span>
                </button>
              ))}
            </div>
            <Button 
              variant="outline"
              onClick={() => goToStep('listing-type')}
              className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        );

      case 'location':
        return (
          <div className="space-y-3 animate-scale-in">
            <CityAutocomplete
              value={data.city}
              onChange={(v) => updateField('city', v)}
              placeholder="Search any city worldwide *"
              fallbackCities={popularCities}
            />
            <AddressAutocomplete 
              value={data.address}
              onChange={(v) => updateField('address', v)}
              placeholder="Street address (optional)"
            />
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('property-type')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => goToStep('details', data.city)}
                disabled={!canProceedFromLocation}
                className="flex-1 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 active:scale-[0.98]"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        );

      case 'details':
        {
          const marketCfg = getMarketConfig((profile as any)?.market);
          const areaLabel = marketCfg.areaUnit === 'sqft' ? 'sqft' : 'm²';
          const currencySymbol = ({
            USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', AUD: 'A$', CAD: 'C$',
            SAR: '﷼', QAR: 'QAR', KWD: 'KWD', BHD: 'BHD', OMR: 'OMR',
          } as Record<string, string>)[marketCfg.currency] || marketCfg.currency;
          const marketAttrs = data.marketAttributes || {};
          const updateAttr = (key: string, value: string | number | boolean) => {
            updateField('marketAttributes', { ...marketAttrs, [key]: value });
          };
        return (
          <div className="space-y-4 animate-scale-in">
            {data.category === 'residential' && (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1 opacity-0 animate-fade-up" style={{ animationDelay: '0s' }}>
                  <Label className="text-xs flex items-center gap-1"><Bed className="w-3 h-3" /> Beds</Label>
                  <Select value={data.bedrooms.toString()} onValueChange={(v) => updateField('bedrooms', parseInt(v))}>
                    <SelectTrigger className="transition-all duration-300 hover:border-primary/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n === 0 ? 'Studio' : n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 opacity-0 animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <Label className="text-xs flex items-center gap-1"><Bath className="w-3 h-3" /> Baths</Label>
                  <Select value={data.bathrooms.toString()} onValueChange={(v) => updateField('bathrooms', parseFloat(v))}>
                    <SelectTrigger className="transition-all duration-300 hover:border-primary/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 1.5, 2, 2.5, 3, 4].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 opacity-0 animate-fade-up" style={{ animationDelay: '0.2s' }}>
                  <Label className="text-xs flex items-center gap-1"><Ruler className="w-3 h-3" /> {areaLabel}</Label>
                  <Input
                    inputMode="decimal"
                    placeholder="120"
                    value={sqmToDisplayArea(data.squareMeters, marketCfg.areaUnit)}
                    onChange={(e) => updateField('squareMeters', displayAreaToSqm(e.target.value, marketCfg.areaUnit))}
                    className="transition-all duration-300 focus:shadow-md focus:shadow-primary/10"
                  />
                </div>
              </div>
            )}
            
            {data.category !== 'residential' && (
              <div className="space-y-1 opacity-0 animate-fade-up">
                <Label className="text-xs flex items-center gap-1"><Ruler className="w-3 h-3" /> Size</Label>
                <Input
                  inputMode="decimal"
                  placeholder={data.category === 'land' ? `e.g., 500 ${areaLabel}` : `e.g., 200 ${areaLabel}`}
                  value={sqmToDisplayArea(data.squareMeters, marketCfg.areaUnit)}
                  onChange={(e) => updateField('squareMeters', displayAreaToSqm(e.target.value, marketCfg.areaUnit))}
                  className="transition-all duration-300 focus:shadow-md focus:shadow-primary/10"
                />
              </div>
            )}
            
            <div className="space-y-1 opacity-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
              <Label className={cn("text-xs flex items-center gap-1", !data.price && "text-destructive")}>
                <DollarSign className="w-3 h-3" /> {data.listingType === 'rent' ? 'Monthly Rent *' : 'Price *'}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{currencySymbol}</span>
                <Input 
                  className={cn("pl-8 transition-all duration-300 focus:shadow-md focus:shadow-primary/10", !data.price && "border-destructive/50")}
                  placeholder={data.listingType === 'rent' ? '1200' : '250000'}
                  value={data.price}
                  onChange={(e) => updateField('price', e.target.value.replace(/[^0-9]/g, ''))}
                />
                {data.listingType === 'rent' && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">/mo</span>
                )}
              </div>
            </div>

            {data.category !== 'land' && (
              <div className="space-y-1 opacity-0 animate-fade-up" style={{ animationDelay: '0.2s' }}>
                <Label className="text-xs">Condition</Label>
                <div className="grid grid-cols-4 gap-1.5">
                  {conditionOptions.map((opt, idx) => (
                    <button
                      key={opt.value}
                      onClick={() => updateField('condition', opt.value as PropertyFormData['condition'])}
                      className={cn(
                        "p-2 rounded-lg text-xs transition-all duration-300 border hover:-translate-y-0.5 hover:shadow-sm active:scale-[0.95]",
                        data.condition === opt.value ? 'border-primary bg-primary/10 shadow-sm shadow-primary/20' : 'border-border hover:border-primary/50 hover:bg-primary/5'
                      )}
                      style={{ animationDelay: `${0.25 + idx * 0.05}s` }}
                    >
                      <span className="block text-base mb-0.5">{opt.emoji}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {marketCfg.attributes.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-border/50 opacity-0 animate-fade-up" style={{ animationDelay: '0.3s' }}>
                <Label className="text-xs text-muted-foreground">
                  {marketCfg.label} details <span className="text-muted-foreground/60">(optional)</span>
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {marketCfg.attributes.map((attr) => {
                    const val = marketAttrs[attr.key];
                    if (attr.type === 'select') {
                      return (
                        <div key={attr.key} className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">{attr.label}</Label>
                          <Select value={(val as string) || ''} onValueChange={(v) => updateAttr(attr.key, v)}>
                            <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              {attr.options?.map((o) => (
                                <SelectItem key={o} value={o}>{o}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    }
                    if (attr.type === 'boolean') {
                      return (
                        <div key={attr.key} className="flex items-center justify-between rounded-md border border-border/50 px-3 py-2">
                          <Label className="text-[11px] text-muted-foreground">{attr.label}</Label>
                          <Switch checked={!!val} onCheckedChange={(c) => updateAttr(attr.key, c)} />
                        </div>
                      );
                    }
                    return (
                      <div key={attr.key} className="space-y-1">
                        <Label className="text-[11px] text-muted-foreground">
                          {attr.label}{attr.suffix ? ` (${attr.suffix})` : ''}
                        </Label>
                        <Input
                          className="h-9 text-xs"
                          type={attr.type === 'number' ? 'number' : 'text'}
                          placeholder={attr.placeholder || ''}
                          value={val === undefined || val === null ? '' : String(val)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (attr.type === 'number') {
                              updateAttr(attr.key, raw === '' ? '' : Number(raw));
                            } else {
                              updateAttr(attr.key, raw);
                            }
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('location')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => goToStep(data.category === 'residential' ? 'furnished' : 'features-quick', `${currencySymbol}${parseInt(data.price || '0').toLocaleString()}`)}
                disabled={!data.price}
                className="flex-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );
        }

      case 'furnished':
        return (
          <div className="space-y-4 animate-scale-in">
            <div className="grid grid-cols-3 gap-3">
              {furnishedOptions.map((opt, idx) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => updateField('furnished', opt.value as FurnishedType)}
                    className={cn(
                      "p-4 rounded-xl text-center transition-all duration-300 border hover:-translate-y-0.5 hover:shadow-md active:scale-[0.95]",
                      data.furnished === opt.value 
                        ? 'border-primary bg-primary/10 shadow-md shadow-primary/20' 
                        : 'border-border hover:border-primary/50 hover:bg-primary/5'
                    )}
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <Icon className={cn(
                      "w-8 h-8 mx-auto mb-2 transition-colors",
                      data.furnished === opt.value ? 'text-primary' : 'text-muted-foreground'
                    )} />
                    <span className="text-sm font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('details')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => goToStep('features-quick', data.furnished ? `${data.furnished.charAt(0).toUpperCase() + data.furnished.slice(1)} furnished` : 'Skipped')}
                className="flex-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'features-quick':
        return (
          <div className="space-y-3">
            {/* Show pre-selected count if any from import */}
            {data.features.length > 0 && (
              <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20 text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                <Check className="w-4 h-4" />
                {data.features.length} features already selected
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {topFeatures.map(feature => {
                const Icon = feature.icon;
                const selected = data.features.includes(feature.id);
                return (
                  <button
                    key={feature.id}
                    onClick={() => toggleFeature(feature.id)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-full text-sm transition-all border min-h-[36px]",
                      selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/50'
                    )}
                  >
                    <Icon className="w-3 h-3" />
                    {feature.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep(data.category === 'residential' ? 'furnished' : 'details')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => goToStep('features-full', `${data.features.length} features`)} variant="outline" className="flex-1">
                See All Features
              </Button>
              <Button onClick={() => goToStep('highlights', `${data.features.length} features`)} className="flex-1">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'features-full':
        return (
          <div className="space-y-4">
            <div className="max-h-[300px] overflow-y-auto space-y-4 pr-2">
              {featureCategories.map(category => (
                <div key={category.id} className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider sticky top-0 bg-background py-1">
                    {category.label}
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {category.features.map(feature => {
                      const Icon = feature.icon;
                      const selected = data.features.includes(feature.id);
                      return (
                        <button
                          key={feature.id}
                          onClick={() => toggleFeature(feature.id)}
                          className={cn(
                            "flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-all border min-h-[32px]",
                            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/50 bg-card'
                          )}
                        >
                          <Icon className="w-3 h-3" />
                          {feature.label}
                          {selected && <Check className="w-3 h-3 ml-0.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 border-t gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('features-quick')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <span className="text-sm text-muted-foreground">
                {data.features.length} selected
              </span>
              <Button onClick={() => goToStep('highlights', `${data.features.length} features`)}>
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'highlights':
        return (
          <div className="space-y-3">
            <Textarea 
              placeholder="e.g., Sea views, newly renovated kitchen, quiet neighborhood..."
              value={data.highlights}
              onChange={(e) => updateField('highlights', e.target.value.slice(0, 1000))}
              className="min-h-[80px] resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{data.highlights?.length || 0}/1000</p>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('features-quick')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => goToStep('description-settings', data.highlights ? 'Added highlights' : 'Skipped')} className="flex-1">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="ghost" onClick={() => goToStep('description-settings', 'Skipped')}>
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );

      case 'description-settings':
        const hasSavedDescriptionSettings = profile?.description_tone || profile?.description_length || profile?.description_custom_instructions;
        const toneLabels: Record<string, string> = { professional: 'Professional', friendly: 'Friendly & Warm', luxury: 'Luxury & Premium', casual: 'Casual & Relaxed' };
        const lengthLabels: Record<string, string> = { short: 'Short & Punchy', medium: 'Balanced', long: 'Detailed' };
        
        // Get saved languages from profile
        const savedLanguages = (profile?.default_languages as DescriptionLanguage[]) || ['en'];
        const savedLanguageDetails = savedLanguages.map(l => allLanguages.find(lang => lang.value === l)).filter(Boolean);
        const canAddMoreLanguages = 4 - savedLanguages.length;
        
        return (
          <div className="space-y-4">
            {hasSavedDescriptionSettings ? (
              <>
                {/* Show saved settings preview */}
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Save className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Your Saved Settings</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    {profile?.description_tone && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Tone:</span>
                        <span className="font-medium">{toneLabels[profile.description_tone] || profile.description_tone}</span>
                      </div>
                    )}
                    {profile?.description_length && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Length:</span>
                        <span className="font-medium">{lengthLabels[profile.description_length] || profile.description_length}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Emojis:</span>
                      <span className="font-medium">{profile?.use_emojis ? '✅ Yes' : '❌ No'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Hashtags:</span>
                      <span className="font-medium">{profile?.include_hashtags ? '✅ Yes' : '❌ No'}</span>
                    </div>
                    {/* Show saved languages */}
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Languages:</span>
                      <div className="flex items-center gap-1.5">
                        {savedLanguageDetails.map(lang => lang && (
                          <span key={lang.value} className="inline-flex items-center gap-0.5 text-xs">
                            {lang.flag}
                          </span>
                        ))}
                        {canAddMoreLanguages > 0 && (
                          <span className="text-xs text-muted-foreground ml-1">
                            (+{canAddMoreLanguages} more allowed)
                          </span>
                        )}
                      </div>
                    </div>
                    {(profile?.focus_features as string[])?.length > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Focus:</span>
                        <span className="font-medium text-xs">{(profile.focus_features as string[]).map(f => f.charAt(0).toUpperCase() + f.slice(1)).join(', ')}</span>
                      </div>
                    )}
                    {profile?.description_custom_instructions && (
                      <div className="pt-2 border-t border-border/50">
                        <span className="text-muted-foreground text-xs">Custom instructions:</span>
                        <p className="text-xs mt-1 italic line-clamp-2">"{profile.description_custom_instructions}"</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Choice cards */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setUseProfileDescriptionSettings(true);
                      // Apply ALL profile settings AND saved languages, then skip to photos
                      onChange({
                        ...data,
                        descriptionTone: profile?.description_tone ?? data.descriptionTone,
                        descriptionLength: profile?.description_length ?? data.descriptionLength,
                        includeHashtags: profile?.include_hashtags ?? data.includeHashtags,
                        customInstructions: profile?.description_custom_instructions ?? data.customInstructions,
                        focusFeatures: (profile?.focus_features as string[]) ?? data.focusFeatures,
                        useEmojis: profile?.use_emojis ?? data.useEmojis,
                        includeAgentInfo: profile?.include_agent_info ?? data.includeAgentInfo,
                        languages: savedLanguages,
                      });
                      goToStep('photos', `Using saved settings (${savedLanguageDetails.map(l => l?.flag).join(' ')})`);
                    }}
                    className={cn(
                      "p-4 rounded-xl border-2 text-center transition-all",
                      "border-primary bg-primary/5 hover:bg-primary/10"
                    )}
                  >
                    <Save className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <p className="font-semibold text-sm">Use Saved</p>
                    <p className="text-xs text-muted-foreground mt-1">Quick & consistent</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUseProfileDescriptionSettings(false);
                      goToStep('description-customize', 'Customizing');
                    }}
                    className={cn(
                      "p-4 rounded-xl border-2 text-center transition-all",
                      "border-border hover:border-primary/50 hover:bg-accent/50"
                    )}
                  >
                    <Pencil className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="font-semibold text-sm">Customize</p>
                    <p className="text-xs text-muted-foreground mt-1">Adjust for this listing</p>
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* No saved settings - show inline options */}
                <Button onClick={() => goToStep('description-customize', 'Customizing')} className="w-full">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}
            
            <Button 
              variant="outline"
              onClick={() => goToStep('highlights')}
              className="w-full transition-all duration-300 hover:shadow-md active:scale-[0.98]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        );

      case 'description-customize':
        const toneOptions = [
          { value: 'professional', label: 'Professional', emoji: '💼' },
          { value: 'friendly', label: 'Friendly & Warm', emoji: '😊' },
          { value: 'luxury', label: 'Luxury & Premium', emoji: '✨' },
          { value: 'casual', label: 'Casual & Relaxed', emoji: '🌴' },
        ];
        const lengthOptions = [
          { value: 'short', label: 'Short', description: '~100 words' },
          { value: 'medium', label: 'Medium', description: '~200 words' },
          { value: 'long', label: 'Long', description: '~350 words' },
        ];
        
        return (
          <div className="space-y-4 animate-scale-in">
            {/* Tone selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Tone</Label>
              <div className="grid grid-cols-2 gap-2">
                {toneOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateField('descriptionTone', opt.value as 'professional' | 'friendly' | 'luxury' | 'casual')}
                    className={cn(
                      "p-3 rounded-lg border text-left transition-all",
                      data.descriptionTone === opt.value 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <span className="text-lg mr-2">{opt.emoji}</span>
                    <span className="text-sm font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Length selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Description Length</Label>
              <div className="grid grid-cols-3 gap-2">
                {lengthOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateField('descriptionLength', opt.value as 'short' | 'medium' | 'long')}
                    className={cn(
                      "p-3 rounded-lg border text-center transition-all",
                      data.descriptionLength === opt.value 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <p className="text-sm font-medium">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.description}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Toggle options */}
            <div className="space-y-3 p-3 rounded-xl bg-accent/50">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Smile className="w-4 h-4 text-primary" />
                  Include Emojis
                </label>
                <Switch 
                  checked={data.useEmojis} 
                  onCheckedChange={(c) => updateField('useEmojis', c)} 
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Hash className="w-4 h-4 text-primary" />
                  Include Hashtags
                </label>
                <Switch 
                  checked={data.includeHashtags ?? true} 
                  onCheckedChange={(c) => updateField('includeHashtags', c)} 
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <UserCircle className="w-4 h-4 text-primary" />
                  Include Contact Info
                </label>
                <Switch 
                  checked={data.includeAgentInfo} 
                  onCheckedChange={(c) => updateField('includeAgentInfo', c)} 
                />
              </div>
            </div>
            
            {/* Emphasize These Features */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Emphasize These Features
              </Label>
              <p className="text-xs text-muted-foreground">AI will highlight these aspects in descriptions</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'location', label: '📍 Location' },
                  { id: 'size', label: '📐 Size & Space' },
                  { id: 'condition', label: '✨ Condition' },
                  { id: 'views', label: '🏔️ Views' },
                  { id: 'amenities', label: '🏊 Amenities' },
                  { id: 'investment', label: '💰 Investment Value' },
                  { id: 'lifestyle', label: '🌴 Lifestyle' },
                  { id: 'architecture', label: '🏛️ Architecture' },
                ].map((feature) => (
                  <Badge
                    key={feature.id}
                    variant={(data.focusFeatures || ['location', 'size', 'condition']).includes(feature.id) ? 'default' : 'outline'}
                    className="cursor-pointer py-2 px-3"
                    onClick={() => {
                      const current = data.focusFeatures || ['location', 'size', 'condition'];
                      const updated = current.includes(feature.id)
                        ? current.filter(f => f !== feature.id)
                        : [...current, feature.id];
                      updateField('focusFeatures', updated);
                    }}
                  >
                    {feature.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Custom instructions */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Custom Instructions (optional)
              </Label>
              <Textarea
                value={data.customInstructions || ''}
                onChange={(e) => updateField('customInstructions', e.target.value)}
                placeholder="E.g., Focus on the sea views, mention proximity to schools..."
                className="min-h-[80px] text-sm"
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('description-settings')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => goToStep('languages', 'Settings customized')} 
                className="flex-1"
              >
                Continue to Languages <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'languages':
        return (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                🌍 Select up to 4 languages for your listing descriptions
              </p>
            </div>
            
            {/* Selected languages preview */}
            {data.languages.length > 0 && (
              <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg">
                <span className="text-xs text-muted-foreground self-center">Selected:</span>
                {data.languages.map(l => {
                  const lang = allLanguages.find(lang => lang.value === l);
                  return lang ? (
                    <span key={l} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full text-sm">
                      {lang.flag} {lang.label}
                    </span>
                  ) : null;
                })}
              </div>
            )}
            
            {/* Popular languages */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Popular Languages</h4>
              <div className="grid grid-cols-2 gap-2">
                {popularLanguages.map(renderLanguageButton)}
              </div>
            </div>
            
            {/* More languages - expandable */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowMoreLanguages(!shouldShowMoreLang)}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                {shouldShowMoreLang ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Hide more languages
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {moreLanguages.length} more languages
                  </>
                )}
              </button>
              
              {shouldShowMoreLang && (
                <div className="grid grid-cols-2 gap-2 max-h-[180px] overflow-y-auto pr-1">
                  {moreLanguages.map(renderLanguageButton)}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {data.languages.length}/4 languages selected
              </p>
              {isMaxLanguages && (
                <span className="text-xs text-amber-600 dark:text-amber-400">Max reached</span>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('description-customize')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => goToStep('photos', `${data.languages.length} languages`)} className="flex-1">
                Continue to Photos <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'photos':
        return (
          <div className="space-y-4">
            {/* Extended photos toggle - choose before uploading */}
            {!isAlreadyOn20PhotoPlan && (
              <div className={cn(
                "p-3 rounded-xl border transition-all",
                useExtendedPhotos 
                  ? "border-primary/40 bg-primary/5" 
                  : extendedPhotosPaymentPending
                    ? "border-amber-400/40 bg-amber-50 dark:bg-amber-950/20"
                    : "border-border/50 bg-card/50"
              )}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <ImageIcon className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Upload up to 20 photos</p>
                      <p className="text-xs text-muted-foreground">
                        Default is 10 · One-time $5 add-on per listing
                      </p>
                    </div>
                  </div>
                  {!extendedPhotosPaymentPending && !useExtendedPhotos && (
                    <Switch
                      checked={false}
                      onCheckedChange={handleExtendedPhotosToggle}
                      disabled={isExtendedPhotosLoading}
                    />
                  )}
                  {useExtendedPhotos && (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      <Check className="w-3 h-3 mr-1" /> Active
                    </Badge>
                  )}
                </div>
                {isExtendedPhotosLoading && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Opening payment...</span>
                  </div>
                )}
                {extendedPhotosPaymentPending && !useExtendedPhotos && (
                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 flex-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Waiting for payment confirmation...</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => {
                        setExtendedPhotosPaymentPending(false);
                        stopPolling();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Photo edits usage indicator */}
            {billing && photos.length > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 border-border">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {photos.length} photo{photos.length !== 1 ? 's' : ''} to enhance
                    </p>
                    {reeditsLimit > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {reeditsRemaining} of {reeditsLimit} re-edits left this month
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {canAddMorePhotos && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files); }}
                onClick={() => document.getElementById('photo-input')?.click()}
                className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-accent/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 group animate-fade-up"
              >
                <input
                  id="photo-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
                <Upload className="w-8 h-8 mx-auto mb-2 text-primary transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1" />
                <p className="font-medium text-sm">Drop photos or click to browse</p>
                <p className="text-xs text-muted-foreground">{maxPhotos - photos.length} slots remaining</p>
              </div>
            )}

            {/* Photo grid with stagger animations */}
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-3">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id} 
                    className="relative group aspect-[4/3] rounded-xl overflow-hidden border-2 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] opacity-0 animate-scale-in"
                    style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
                  >
                    <img src={photo.preview} alt={`Photo ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-primary text-primary-foreground text-[10px] sm:text-xs font-bold shadow-md">
                      #{index + 1}
                    </div>
                    <button
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-7 h-7 sm:w-6 sm:h-6 rounded-full bg-background/90 flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 hover:bg-destructive hover:text-destructive-foreground active:scale-90"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* AI Photo Analysis Status */}
            {photos.length > 0 && (
              <div className={cn(
                "flex items-center gap-2.5 p-3 rounded-lg border animate-fade-up",
                isAnalyzingPhotos 
                  ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800/50 text-blue-800 dark:text-blue-200"
                  : photoAnalysisResult 
                    ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800/50 text-green-800 dark:text-green-200"
                    : "bg-muted/50 border-muted-foreground/20"
              )}>
                {isAnalyzingPhotos ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                    <p className="text-xs leading-relaxed flex-1">
                      <span className="font-medium">AI is analyzing your photos...</span> Detecting property type, condition, and features.
                    </p>
                  </>
                ) : photoAnalysisResult ? (
                  <>
                    <Sparkles className="h-4 w-4 flex-shrink-0" />
                    <p className="text-xs leading-relaxed flex-1">
                      <span className="font-medium">AI detected:</span> {photoAnalysisResult.suggestedPropertyType} • {photoAnalysisResult.suggestedCondition} condition 
                      {photoAnalysisResult.visibleFeatures.length > 0 && ` • ${photoAnalysisResult.visibleFeatures.length} features`}
                      {photoAnalysisResult.confidence < 0.7 && " (low confidence - please verify)"}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setHasTriedAnalysis(false); runPhotoAnalysis(); }}
                      className="h-7 text-xs px-2"
                    >
                      Re-analyze
                    </Button>
                  </>
                ) : hasTriedAnalysis ? (
                  <>
                    <Info className="h-4 w-4 flex-shrink-0" />
                    <p className="text-xs leading-relaxed flex-1">
                      Could not auto-detect property details. Please fill in manually.
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setHasTriedAnalysis(false); runPhotoAnalysis(); }}
                      className="h-7 text-xs px-2"
                    >
                      Try again
                    </Button>
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 flex-shrink-0" />
                    <p className="text-xs leading-relaxed flex-1">
                      AI can analyze photos to auto-detect property type, condition & features
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={runPhotoAnalysis}
                      className="h-7 text-xs px-2"
                    >
                      Analyze
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Watermark warning */}
            {photos.length > 0 && (
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-200 animate-fade-up">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p className="text-xs leading-relaxed">
                  <span className="font-medium">Note:</span> Photos with existing watermarks may not enhance perfectly. For best results, use original photos without watermarks.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('languages')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => goToStep('enhancement-style', `${photos.length} photos`)}
                disabled={photos.length === 0}
                className="flex-1 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 active:scale-[0.98] group"
              >
                Enhance Photos <Sparkles className="w-4 h-4 ml-2 transition-transform group-hover:rotate-12" />
              </Button>
            </div>
          </div>
        );

      case 'enhancement-style':
        // Check if user has a saved profile template
        const hasSavedTemplate = profile?.photo_enhancement_template && Object.keys(profile.photo_enhancement_template).length > 0;
        
        // Get the saved preset name if available
        const getSavedPresetName = () => {
          const template = profile?.photo_enhancement_template;
          if (!template?.advancedSettings) return null;
          
          // Match against known presets
          const { brightness, saturation, warmth } = template.advancedSettings;
          if (brightness === 5 && saturation === 10 && warmth === 0) return 'Natural & Clean';
          if (brightness === 20 && saturation === 15 && warmth === 5) return 'Bright & Airy';
          if (brightness === 10 && saturation === 25 && warmth === 15) return 'Warm & Inviting';
          if (brightness === 15 && saturation === 30 && warmth === 5) return 'Magazine Ready';
          return 'Custom';
        };
        
        const savedPresetName = getSavedPresetName();
        
        return (
          <div className="space-y-4">
            {/* Info about enhancement styles */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
              <div className="flex items-start gap-3">
                <Camera className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Photo Enhancement Style</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Choose how your photos will be enhanced. You can save your preferred style in your profile for future listings.
                  </p>
                </div>
              </div>
            </div>

            {/* Option 1: Use saved profile style (if available) */}
            {hasSavedTemplate && (
              <button
                onClick={() => {
                  setUseProfileStyle(true);
                  setSelectedPresetId(null);
                  // Apply profile template settings
                  const template = profile?.photo_enhancement_template;
                  if (template) {
                    setEnhancementOptions(prev => ({
                      ...prev,
                      addDecorations: template.addDecorations ?? false,
                      skyMode: template.skyMode,
                      declutterLevel: template.declutterLevel,
                      advancedSettings: template.advancedSettings,
                    }));
                  }
                  goToStep('enhancement-choice', 'Using my saved style');
                }}
                className={cn(
                  "w-full p-4 rounded-xl border-2 text-left transition-all hover:shadow-md",
                  "border-primary bg-primary/5 hover:bg-primary/10"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Save className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Use My Saved Style</span>
                      <Badge variant="secondary" className="text-xs">Recommended</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {savedPresetName ? `${savedPresetName} preset` : 'Your custom settings'}
                      {profile?.photo_enhancement_template?.skyMode && profile.photo_enhancement_template.skyMode !== 'off' && 
                        ` • ${profile.photo_enhancement_template.skyMode === 'sunset' ? 'Sunset' : 'Blue'} sky`}
                      {profile?.photo_enhancement_template?.declutterLevel && 
                        ` • ${profile.photo_enhancement_template.declutterLevel.charAt(0).toUpperCase() + profile.photo_enhancement_template.declutterLevel.slice(1)} declutter`}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-primary mt-1" />
                </div>
              </button>
            )}

            {/* Option 2: Choose a different style */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">
                {hasSavedTemplate ? 'Or choose a different style for this listing:' : 'Choose an enhancement style:'}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {photoEnhancementPresets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => {
                      setUseProfileStyle(false);
                      setSelectedPresetId(preset.id);
                      // Apply the preset settings to enhancement options
                      setEnhancementOptions(prev => ({
                        ...prev,
                        advancedSettings: preset.settings,
                      }));
                      goToStep('enhancement-choice', preset.name);
                    }}
                    className={cn(
                      "p-4 rounded-xl border-2 text-left transition-all hover:shadow-lg hover:-translate-y-0.5",
                      preset.bgColor,
                      preset.borderColor,
                      preset.hoverBg
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{preset.icon}</span>
                      <div className="flex-1">
                        <span className="font-semibold">{preset.name}</span>
                        <p className="text-xs text-muted-foreground mt-1">
                          {preset.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Back button */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('photos')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </div>
          </div>
        );

      case 'enhancement-choice':
        const isStagingActive = isAnyStagingActive;
        const isGoldenHourActive = goldenHourPhotoIds.size > 0;
        const isFullStagingActive = fullStagingPhotoIds.size > 0;
        const stagingModeByPhoto = (photoId: string) => getStagingMode(photoId, stagingPhotoIds, fullStagingPhotoIds);
        const setStagingModeForPhoto = (photoId: string, mode: StagingMode) => {
          setIdsForMode(photoId, mode, setStagingPhotoIds, setFullStagingPhotoIds);
        };
        
        const toggleGoldenHourForPhoto = (photoId: string) => {
          setGoldenHourPhotoIds(prev => {
            const next = new Set(prev);
            if (next.has(photoId)) next.delete(photoId);
            else next.add(photoId);
            return next;
          });
        };

        const toggleAllStaging = (on: boolean) => {
          if (on) {
            setStagingPhotoIds(new Set(photos.map(p => p.id)));
            setFullStagingPhotoIds(new Set());
          } else {
            setStagingPhotoIds(new Set());
            setFullStagingPhotoIds(new Set());
          }
        };
        
        const toggleAllGoldenHour = (on: boolean) => {
          setGoldenHourPhotoIds(on ? new Set(photos.map(p => p.id)) : new Set());
        };

        return (
          <div className="space-y-4">
            <ReeditQuotaBanner />
            {/* AI info box */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
              <div className="flex items-start gap-3">
                <Wand2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">AI-Powered Enhancement</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Our AI will automatically optimize brightness, colors, exposure, and clean up imperfections while keeping your photos natural and truthful.
                  </p>
                </div>
              </div>
            </div>

            {/* Staging selector header */}
            <div className="flex items-center gap-3 p-4 rounded-xl border bg-card">
              <Lamp className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <Label className="text-sm font-medium">Virtual Staging</Label>
                <p className="text-xs text-muted-foreground">Choose decorative accents for furnished rooms or full furniture for empty rooms on the photo grid.</p>
              </div>
              <Switch
                checked={isStagingActive}
                onCheckedChange={toggleAllStaging}
              />
            </div>

            {/* Golden Hour toggle header */}
            <div className="flex items-center gap-3 p-4 rounded-xl border bg-card">
              <Sunset className="w-5 h-5 text-orange-500" />
              <div className="flex-1">
                <Label className="text-sm font-medium">Golden Hour Ambiance</Label>
                <p className="text-xs text-muted-foreground">Transform to romantic sunset atmosphere with warm lighting</p>
              </div>
              <Switch
                checked={isGoldenHourActive}
                onCheckedChange={toggleAllGoldenHour}
              />
            </div>

            {isFullStagingActive && (
              <div className="rounded-xl border bg-card p-4 space-y-2">
                <Label className="text-xs text-muted-foreground">Furniture style for empty rooms</Label>
                <Select
                  value={fullStagingGlobalStyle}
                  onValueChange={(val) => setFullStagingGlobalStyle(val as 'modern' | 'scandinavian' | 'coastal' | 'luxury' | 'minimalist')}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="scandinavian">Scandinavian</SelectItem>
                    <SelectItem value="coastal">Coastal</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                    <SelectItem value="minimalist">Minimalist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isFullStagingActive && (() => {
              const ROOM_OPTIONS: { value: FullStagingRoomType; label: string; emoji: string }[] = [
                { value: 'living',   label: 'Living',   emoji: '🛋️' },
                { value: 'bedroom',  label: 'Bedroom',  emoji: '🛏️' },
                { value: 'dining',   label: 'Dining',   emoji: '🍽️' },
                { value: 'office',   label: 'Office',   emoji: '💼' },
                { value: 'kitchen',  label: 'Kitchen',  emoji: '🍳' },
                { value: 'bathroom', label: 'Bathroom', emoji: '🛁' },
                { value: 'balcony',  label: 'Balcony',  emoji: '🌇' },
                { value: 'hallway',  label: 'Hallway',  emoji: '🚪' },
                { value: 'other',    label: 'Other',    emoji: '📦' },
              ];
              const fullStagedPhotos = photos.filter(p => fullStagingPhotoIds.has(p.id));
              return (
                <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/20 p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <Home className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <Label className="text-sm font-semibold text-foreground">
                        Which room is this? ({fullStagedPhotos.length} to confirm)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Tap the correct room for each empty photo so the AI adds the right furniture.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {fullStagedPhotos.map((photo) => {
                      const idx = photos.findIndex(p => p.id === photo.id);
                      const selected = fullStagingRoomTypes[photo.id] || 'living';
                      return (
                        <div key={photo.id} className="rounded-lg bg-background border p-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <img
                              src={photo.preview}
                              alt={`Photo ${idx + 1}`}
                              className="w-14 h-14 rounded-md object-cover border flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium">Photo {idx + 1}</p>
                              <p className="text-[11px] text-muted-foreground">
                                Selected: <span className="text-foreground font-medium">
                                  {ROOM_OPTIONS.find(r => r.value === selected)?.emoji}{' '}
                                  {ROOM_OPTIONS.find(r => r.value === selected)?.label}
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="-mx-2 px-2 overflow-x-auto scrollbar-none">
                            <div className="flex gap-1.5 pb-1 min-w-max">
                              {ROOM_OPTIONS.map(opt => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setFullStagingRoomTypes(prev => ({ ...prev, [photo.id]: opt.value }))}
                                  className={cn(
                                    "min-h-[44px] px-3 rounded-full text-xs font-medium border transition-all whitespace-nowrap flex items-center gap-1.5",
                                    selected === opt.value
                                      ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                                      : "bg-background text-foreground border-border hover:border-emerald-500/50"
                                  )}
                                >
                                  <span className="text-sm leading-none">{opt.emoji}</span>
                                  <span>{opt.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Per-photo selection grid - shown when either feature is active */}
            {(isStagingActive || isGoldenHourActive) && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Set one staging type per photo: none, decorative accents for furnished rooms, or full furniture for empty rooms. Golden Hour can be combined with either.
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photos.map((photo, index) => {
                    const mode = stagingModeByPhoto(photo.id);
                    return (
                    <div key={photo.id} className={cn(
                      "relative group rounded-lg overflow-hidden border bg-muted aspect-square",
                      mode === 'decorative' ? 'border-primary' : mode === 'full' ? 'border-emerald-500' : 'border-border'
                    )}>
                      <img
                        src={photo.preview}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-x-1 bottom-1 space-y-1">
                        {isStagingActive && (
                          <div className="grid grid-cols-3 rounded-md overflow-hidden bg-background/85 shadow-sm">
                            <button
                              type="button"
                              onClick={() => setStagingModeForPhoto(photo.id, 'none')}
                              className={cn("h-8 flex items-center justify-center", mode === 'none' ? "bg-muted text-foreground" : "text-muted-foreground")}
                              title="No staging"
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setStagingModeForPhoto(photo.id, 'decorative')}
                              className={cn("h-8 flex items-center justify-center", mode === 'decorative' ? "bg-primary text-primary-foreground" : "text-primary")}
                              title="Decorative staging for furnished rooms"
                            >
                              <Lamp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setStagingModeForPhoto(photo.id, 'full')}
                              className={cn("h-8 flex items-center justify-center", mode === 'full' ? "bg-emerald-600 text-primary-foreground" : "text-emerald-600")}
                              title="Full staging for empty rooms"
                            >
                              <Home className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        {isGoldenHourActive && (
                          <button
                            type="button"
                            onClick={() => toggleGoldenHourForPhoto(photo.id)}
                            className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center transition-all mx-auto shadow-sm",
                              goldenHourPhotoIds.has(photo.id)
                                ? "bg-orange-500 text-white shadow-md scale-110"
                                : "bg-background/85 text-orange-500 hover:bg-background"
                            )}
                            title={goldenHourPhotoIds.has(photo.id) ? "Remove golden hour" : "Add golden hour"}
                          >
                            <Sunset className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <span className="absolute top-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                        {index + 1}
                      </span>
                    </div>
                  )})}
                </div>
                <p className="text-[11px] text-muted-foreground text-center">
                  {isStagingActive && `🌿 Decoration: ${stagingPhotoIds.size}/${photos.length} · 🏠 Full Staging: ${fullStagingPhotoIds.size}/${photos.length}`}
                  {isStagingActive && isGoldenHourActive && ' · '}
                  {isGoldenHourActive && `🌅 Golden Hour: ${goldenHourPhotoIds.size}/${photos.length}`}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => goToStep('enhancement-style')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => goToStep('photo-instructions', isAnyStagingActive ? 'With staging' : 'No staging')} 
                className="flex-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'enhance-options':
        // Same per-photo selection as enhancement-choice
        const isStagingActiveAlt = stagingPhotoIds.size > 0;
        const isGoldenHourActiveAlt = goldenHourPhotoIds.size > 0;
        return (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
              <div className="flex items-start gap-3">
                <Wand2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">AI-Powered Enhancement</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Our AI will automatically optimize brightness, colors, exposure, and clean up imperfections while keeping your photos natural and truthful.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl border bg-card">
              <Lamp className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <Label className="text-sm font-medium">Virtual Decoration</Label>
                <p className="text-xs text-muted-foreground">For furnished or partially-furnished rooms — accents like towels, pillows, plants</p>
              </div>
              <Switch
                checked={isStagingActiveAlt}
                onCheckedChange={(on) => setStagingPhotoIds(on ? new Set(photos.map(p => p.id)) : new Set())}
              />
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl border bg-card">
              <Sunset className="w-5 h-5 text-orange-500" />
              <div className="flex-1">
                <Label className="text-sm font-medium">Golden Hour Ambiance</Label>
                <p className="text-xs text-muted-foreground">Transform to romantic sunset atmosphere with warm lighting</p>
              </div>
              <Switch
                checked={isGoldenHourActiveAlt}
                onCheckedChange={(on) => setGoldenHourPhotoIds(on ? new Set(photos.map(p => p.id)) : new Set())}
              />
            </div>

            {/* Per-photo grid */}
            {(isStagingActiveAlt || isGoldenHourActiveAlt) && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Tap icons to select which photos get each effect:</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {photos.map((photo, index) => (
                    <div key={photo.id} className="relative rounded-lg overflow-hidden border border-border bg-muted aspect-square">
                      <img src={photo.preview} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 inset-x-0 flex justify-center gap-1 p-1.5 bg-gradient-to-t from-black/60 to-transparent">
                        {isStagingActiveAlt && (
                          <button type="button" onClick={() => {
                            setStagingPhotoIds(prev => {
                              const next = new Set(prev);
                              next.has(photo.id) ? next.delete(photo.id) : next.add(photo.id);
                              return next;
                            });
                          }} className={cn(
                            "w-7 h-7 rounded-full flex items-center justify-center transition-all",
                            stagingPhotoIds.has(photo.id) ? "bg-primary text-primary-foreground shadow-md scale-110" : "bg-white/20 text-white/50 hover:bg-white/30"
                          )}>
                            <Lamp className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {isGoldenHourActiveAlt && (
                          <button type="button" onClick={() => {
                            setGoldenHourPhotoIds(prev => {
                              const next = new Set(prev);
                              next.has(photo.id) ? next.delete(photo.id) : next.add(photo.id);
                              return next;
                            });
                          }} className={cn(
                            "w-7 h-7 rounded-full flex items-center justify-center transition-all",
                            goldenHourPhotoIds.has(photo.id) ? "bg-orange-500 text-white shadow-md scale-110" : "bg-white/20 text-white/50 hover:bg-white/30"
                          )}>
                            <Sunset className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <span className="absolute top-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-full">{index + 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={() => goToStep('photo-instructions', 'Settings configured')} className="w-full">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'media-generation':
        return (
          <div className="space-y-4">
            {/* Info box */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/5 to-blue-500/10 border border-purple-500/20">
              <div className="flex items-start gap-3">
                <Video className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Media Generation</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create a stunning PDF flyer with property info and a cinematic video for social media.
                  </p>
                </div>
              </div>
            </div>

            {/* PDF Flyer toggle */}
            <div className="flex items-center gap-3 p-4 rounded-xl border bg-card">
              <FileText className="w-5 h-5 text-blue-500" />
              <div className="flex-1">
                <Label className="text-sm font-medium">PDF Flyer</Label>
                <p className="text-xs text-muted-foreground">Beautiful photo grid with price, bedrooms & details - perfect for social media</p>
              </div>
              <Switch
                checked={enhancementOptions.generateProfilePicture || false}
                onCheckedChange={(checked) => setEnhancementOptions(prev => ({ ...prev, generateProfilePicture: checked }))}
              />
            </div>

            {/* Video toggle */}
            <div className="flex items-center gap-3 p-4 rounded-xl border bg-card">
              <Video className="w-5 h-5 text-purple-500" />
              <div className="flex-1">
                <Label className="text-sm font-medium">Slideshow Video</Label>
                <p className="text-xs text-muted-foreground">Fast browser-generated slideshow with smooth transitions — free</p>
              </div>
              <Switch
                checked={enhancementOptions.generateVideo || false}
                onCheckedChange={(checked) => setEnhancementOptions(prev => ({ ...prev, generateVideo: checked }))}
              />
            </div>

            {/* Cinematic AI Video upsell — paid, generated post-save */}
            <div className={cn(
              "p-4 rounded-xl border transition-colors",
              wantsCinematicVideo
                ? "border-purple-500/60 bg-gradient-to-br from-purple-500/10 to-blue-500/15 shadow-sm"
                : "border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-blue-500/10"
            )}>
              <div className="flex items-center gap-3">
                <Clapperboard className="w-5 h-5 text-purple-500" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-sm font-medium">Cinematic AI Video</Label>
                    <span className="text-[10px] uppercase tracking-wide bg-purple-500/15 text-purple-600 dark:text-purple-300 px-1.5 py-0.5 rounded">Premium · $5</span>
                    <button
                      type="button"
                      onClick={() => window.open('/cinematic-video', '_blank', 'noopener')}
                      className="text-[11px] text-purple-600 dark:text-purple-300 underline underline-offset-2"
                    >
                      See how Cinematic AI Video works
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Real AI camera motion per photo (dolly, orbit, pan). Pay $5 now — your cinematic video is generated automatically once your listing is created.
                  </p>
                </div>
                <Switch
                  checked={wantsCinematicVideo}
                  onCheckedChange={handleCinematicToggle}
                  disabled={!isSubscriber || isCinematicCheckoutLoading || cinematicPaymentPending}
                />
              </div>
              {cinematicPaymentPending && (
                <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-900 dark:text-purple-100">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-purple-600 animate-pulse" />
                  <p className="text-[11px] leading-relaxed">
                    Waiting for payment confirmation… complete checkout in the new tab and this will unlock automatically.
                  </p>
                </div>
              )}
              {wantsCinematicVideo && !cinematicPaymentPending && (
                <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-900 dark:text-purple-100">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-purple-600" />
                  <p className="text-[11px] leading-relaxed">
                    Cinematic AI Video is paid and ready — we'll generate it automatically as soon as your listing is created.
                  </p>
                </div>
              )}
            </div>

            {/* Time note if either is selected */}
            {(enhancementOptions.generateProfilePicture || enhancementOptions.generateVideo) && (
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-muted/50 border text-muted-foreground animate-fade-up">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p className="text-xs leading-relaxed">
                  <span className="font-medium">Note:</span> These are generated in your browser. PDF flyers take a few seconds and videos take ~10-20 seconds.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => goToStep('photo-instructions')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => goToStep('watermark-options', 
                  enhancementOptions.generateProfilePicture && enhancementOptions.generateVideo 
                    ? 'PDF Flyer + Video' 
                    : enhancementOptions.generateProfilePicture 
                      ? 'PDF Flyer' 
                      : enhancementOptions.generateVideo 
                        ? 'Video' 
                        : 'No media'
                )} 
                className="flex-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'photo-instructions':
        return (
          <div className="space-y-4">
            {/* Individual Photo Instructions Button */}
            <button
              onClick={() => {
                setPerPhotoPromptsEnabled(true);
                setEditingPhotoIndex(0);
                setShowPerPhotoEditor(true);
              }}
              className="w-full p-4 rounded-xl border-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary/5 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Wand2 className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-sm">Add Custom Instructions Per Photo</p>
                  <p className="text-xs text-muted-foreground">
                    {Object.keys(perPhotoPrompts).filter(k => perPhotoPrompts[k]?.trim()).length > 0 
                      ? `${Object.keys(perPhotoPrompts).filter(k => perPhotoPrompts[k]?.trim()).length} of ${photos.length} photos have instructions`
                      : "Click to add specific instructions for each photo"}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
            </button>

            {/* Per-Photo Editor Modal */}
            {showPerPhotoEditor && photos.length > 0 && (
              <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                  {/* Header */}
                  <div className="border-b border-border px-6 py-4 flex items-center justify-between bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Wand2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Custom Instructions for Photo {editingPhotoIndex + 1}</h3>
                        <p className="text-xs text-muted-foreground">{editingPhotoIndex + 1} of {photos.length} photos</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowPerPhotoEditor(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Photo Preview */}
                  <div className="flex-1 overflow-auto p-6 space-y-4">
                    <div className="relative aspect-video bg-muted rounded-xl overflow-hidden">
                      <img
                        src={photos[editingPhotoIndex]?.preview}
                        alt={`Photo ${editingPhotoIndex + 1}`}
                        className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">#{editingPhotoIndex + 1}</span>
                        </div>
                    </div>

                    {/* Instructions Input */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        What would you like to add or change in this photo?
                      </Label>
                      <Textarea
                        placeholder="Examples:\n• Add fresh flowers on the table\n• Make the pool water look crystal clear\n• Add cozy throw pillows on the sofa\n• Leave empty to skip (standard enhancement only)"
                        value={perPhotoPrompts[photos[editingPhotoIndex]?.id] || ''}
                        onChange={(e) => {
                          const photoId = photos[editingPhotoIndex]?.id;
                          if (photoId) {
                            setPerPhotoPrompts(prev => ({
                              ...prev,
                              [photoId]: e.target.value
                            }));
                          }
                        }}
                        className="min-h-[100px] resize-none"
                      />
                      <p className="text-xs text-muted-foreground">
                        Be specific about what you want. The room structure, walls, floors, and windows will always be preserved.
                      </p>
                    </div>
                  </div>

                  {/* Navigation Footer */}
                  <div className="border-t border-border px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 bg-muted/30">
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 px-2 sm:px-4"
                      onClick={() => {
                        if (editingPhotoIndex > 0) {
                          setEditingPhotoIndex(editingPhotoIndex - 1);
                        }
                      }}
                      disabled={editingPhotoIndex === 0}
                      aria-label="Previous photo"
                    >
                      <ArrowLeft className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>

                    <div className="flex items-center gap-1.5 overflow-x-auto px-1 min-w-0 flex-1 justify-center">
                      {photos.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setEditingPhotoIndex(index)}
                          aria-label={`Go to photo ${index + 1}`}
                          className={cn(
                            "h-2 rounded-full transition-all shrink-0",
                            index === editingPhotoIndex ? "bg-primary w-4" : "bg-muted-foreground/30 hover:bg-muted-foreground/50 w-2"
                          )}
                        />
                      ))}
                    </div>

                    {editingPhotoIndex < photos.length - 1 ? (
                      <Button
                        size="sm"
                        className="shrink-0 px-2 sm:px-4"
                        onClick={() => setEditingPhotoIndex(editingPhotoIndex + 1)}
                        aria-label="Next photo"
                      >
                        <span className="hidden sm:inline">
                          {perPhotoPrompts[photos[editingPhotoIndex]?.id]?.trim() ? 'Next' : 'Skip'}
                        </span>
                        <ArrowRight className="h-4 w-4 sm:ml-2" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="shrink-0 px-2 sm:px-4"
                        onClick={() => {
                          setPerPhotoPromptsEnabled(true);
                          setShowPerPhotoEditor(false);
                        }}
                      >
                        <Check className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Done</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => goToStep('enhancement-choice')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => goToStep('media-generation', Object.values(perPhotoPrompts).some(p => p?.trim()) ? 'Custom instructions added' : 'Skipped')} className="flex-1">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="ghost" onClick={() => goToStep('media-generation', 'Skipped')}>
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );

      case 'watermark-options':
        return (
          <div className="space-y-3">
            {canUseWatermark ? (
              <>
                {/* Add custom watermark */}
                <div className="p-3 rounded-lg border bg-card/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <Stamp className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Add your watermark / branding</Label>
                  </div>
                  <input
                    ref={watermarkInputRef}
                    type="file"
                    accept="image/png,image/svg+xml"
                    className="hidden"
                    onChange={handleWatermarkUpload}
                  />
                  {customWatermark ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                        <img src={customWatermark} alt="Watermark" className="h-8 max-w-[60px] object-contain" />
                        <select
                          value={watermarkPosition}
                          onChange={(e) => setWatermarkPosition(e.target.value as typeof watermarkPosition)}
                          className="text-xs bg-background border rounded px-2 py-1 flex-1"
                        >
                          <option value="top-left">Top Left</option>
                          <option value="top-right">Top Right</option>
                          <option value="bottom-left">Bottom Left</option>
                          <option value="bottom-right">Bottom Right</option>
                          <option value="center">Center</option>
                        </select>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={removeCustomWatermark}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Opacity</span>
                          <span>{watermarkOpacity}%</span>
                        </div>
                        <Slider
                          value={[watermarkOpacity]}
                          onValueChange={([value]) => setWatermarkOpacity(value)}
                          min={20}
                          max={100}
                          step={5}
                          className="h-1"
                        />
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => watermarkInputRef.current?.click()}>
                      <Upload className="h-3 w-3 mr-2" />
                      Upload (PNG/SVG)
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 space-y-2">
                <div className="flex items-center gap-2">
                  <Stamp className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <Label className="text-sm font-medium text-amber-700 dark:text-amber-300">Watermark Feature</Label>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Custom watermarks are available on Growth and Team plans. Upgrade to add your branding to photos.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => goToStep('media-generation')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => goToStep('final-review', customWatermark ? 'Watermark added' : 'No watermark')} className="flex-1">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="ghost" onClick={() => goToStep('final-review', 'Skipped')}>
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );

      case 'advanced-settings':
        // Simplified - just go to final review
        return (
          <div className="space-y-3">
            <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
              <div className="flex items-start gap-3">
                <Wand2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">AI Enhancement Ready</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The AI will automatically optimize your photos with professional settings.
                  </p>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => goToStep('final-review', 'Ready to generate')}
              className="w-full"
            >
              Review & Generate <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'final-review': {
        const _reviewMarket = (profile as any)?.market;
        const _reviewMcfg = getMarketConfig(_reviewMarket);
        const _reviewArea = sqmToDisplayArea(data.squareMeters, _reviewMcfg.areaUnit);
        const _reviewAreaLabel = _reviewMcfg.areaUnit === 'sqft' ? 'sqft' : 'm²';
        const _reviewPrice = data.price
          ? formatPriceForMarket(parseFloat(data.price), _reviewMarket)
          : '';
        return (
          <div className="space-y-4 animate-scale-in">
            {/* Photo strip with animation */}
            {photos.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-2">
                {photos.map((photo, idx) => (
                  <div 
                    key={photo.id} 
                    className="relative flex-shrink-0 w-14 h-10 rounded-lg overflow-hidden border border-border opacity-0 animate-fade-up transition-all duration-300 hover:scale-110 hover:border-primary/50"
                    style={{ animationDelay: `${Math.min(idx * 0.03, 0.2)}s` }}
                  >
                    <img src={photo.preview} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            {/* Summary card with entrance animation */}
            <div className="p-4 rounded-xl bg-accent/50 space-y-3 opacity-0 animate-fade-up shadow-lg" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-1">
                  <p className="font-semibold">{data.type ? getPropertyTypes().find(t => t.value === data.type)?.label : 'Property'} in {data.city}</p>
                  <p className="text-sm text-muted-foreground">
                    {data.category === 'residential' && `${data.bedrooms} bed, ${data.bathrooms} bath • `}
                    {_reviewArea && `${_reviewArea} ${_reviewAreaLabel} • `}
                    {data.listingType === 'rent' ? 'For Rent' : 'For Sale'}
                  </p>
                  <p className="text-lg font-bold text-primary">
                    {_reviewPrice || (data.price ? `${parseInt(data.price).toLocaleString()}` : '')}{data.listingType === 'rent' ? '/mo' : ''}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t">
                <div><span className="text-muted-foreground">Photos:</span> <span className="font-medium">{photos.length}</span></div>
                <div><span className="text-muted-foreground">Features:</span> <span className="font-medium">{data.features.length}</span></div>
                <div><span className="text-muted-foreground">Languages:</span> <span className="font-medium">{data.languages.map(l => allLanguages.find(lang => lang.value === l)?.flag).join(' ')}</span></div>
                <div><span className="text-muted-foreground">Decoration:</span> <span className="font-medium">{enhancementOptions.addDecorations ? 'Yes' : 'No'}</span></div>
                <div><span className="text-muted-foreground">Enhancement:</span> <span className="font-medium">{selectedPresetId ? photoEnhancementPresets.find(p => p.id === selectedPresetId)?.name : 'AI-Powered'}</span></div>
                {enhancementOptions.goldenHourMode && <div><span className="text-muted-foreground">Golden Hour:</span> <span className="font-medium">Yes</span></div>}
                {customPrompt && <div className="col-span-2"><span className="text-muted-foreground">Custom Prompt:</span> <span className="font-medium truncate block">{customPrompt}</span></div>}
                {customWatermark && <div><span className="text-muted-foreground">Watermark:</span> <span className="font-medium">Yes</span></div>}
                {data.descriptionTone && <div><span className="text-muted-foreground">Tone:</span> <span className="font-medium capitalize">{data.descriptionTone}</span></div>}
                {data.descriptionLength && <div><span className="text-muted-foreground">Length:</span> <span className="font-medium capitalize">{data.descriptionLength}</span></div>}
              </div>
            </div>

            {/* Usage consumption preview */}
            {billing && (
              <div className="p-3 rounded-xl border space-y-2 opacity-0 animate-fade-up bg-muted/30 border-border" style={{ animationDelay: '0.12s' }}>
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-muted-foreground" />
                  <p className="text-xs font-medium">This listing will use:</p>
                </div>
                <div className="flex items-center gap-1.5 p-2 rounded-lg bg-background/50 text-xs">
                  <Home className="w-3 h-3" />
                  <div>
                    <p className="font-medium">1 property</p>
                    <p className="text-muted-foreground">
                      {propertiesRemaining === 'unlimited' ? '∞ left' : `${propertiesRemaining} left`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email notification */}
            {userEmail && (
              <div className="flex items-center gap-3 p-3 rounded-xl border bg-card opacity-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
                <Mail className="w-5 h-5 text-primary" />
                <div className="flex-1">
                  <Label className="text-sm">Email me when ready</Label>
                  <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                </div>
                <Switch checked={processInBackground} onCheckedChange={setProcessInBackground} />
              </div>
            )}

            <div className="flex gap-2 opacity-0 animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <Button 
                variant="outline"
                onClick={() => goToStep('watermark-options')}
                className="transition-all duration-300 hover:shadow-md active:scale-[0.98]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={handleGenerate}
                disabled={false}
                className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] group"
                size="lg"
              >
                <Sparkles className="w-5 h-5 mr-2 transition-transform group-hover:rotate-12 group-hover:scale-110" />
                Create Listing ✨
              </Button>
            </div>
           </div>
         );
      }

      default:
        return null;
    }
  };

  // Calculate progress - different step order for import vs manual
  const isImportFlow = currentStep === 'import-url' || currentStep === 'import-review' || 
    (messages.some(m => m.content.includes('Importing:')));
  
  const importStepOrder: FlowStep[] = ['method', 'import-url', 'import-review', 'description-settings', 'photos', 'enhancement-choice', 'photo-instructions', 'media-generation', 'watermark-options', 'final-review'];
  const manualStepOrder: FlowStep[] = ['method', 'category', 'listing-type', 'property-type', 'location', 'details', 'features-quick', 'highlights', 'description-settings', 'photos', 'enhancement-choice', 'photo-instructions', 'media-generation', 'watermark-options', 'final-review'];
  
  const stepOrder = isImportFlow ? importStepOrder : manualStepOrder;
  const currentProgress = Math.max(0, stepOrder.indexOf(currentStep));
  const totalSteps = stepOrder.length;

  return (
    <div className="flex flex-col h-full max-h-[calc(100dvh-160px)]">
      {/* Progress bar with plan info */}
      <div className="px-4 py-3 border-b">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <div className="flex items-center gap-2">
            <span className="font-medium">Creating Your Listing</span>
            {billing && tierConfig && (
              <Badge variant="outline" className={cn(
                "text-[10px] h-5 gap-1",
                billing.subscriptionTier === 'studio' ? "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800" :
                billing.subscriptionTier === 'growth' ? "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800" :
                billing.subscriptionTier === 'starter' ? "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800" :
                "bg-muted"
              )}>
                <TierIcon className="w-3 h-3" />
                {tierConfig.displayName}
              </Badge>
            )}
          </div>
          <span className="tabular-nums">{Math.min(currentProgress + 1, totalSteps)}/{totalSteps}</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden relative">
          <div 
            className="h-full bg-gradient-to-r from-primary via-primary/90 to-primary/70 transition-all duration-700 ease-out relative"
            style={{ width: `${((currentProgress + 1) / totalSteps) * 100}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </div>
        </div>
      </div>

      {/* Compact usage indicator */}
      {billing && currentStep !== 'method' && currentStep !== 'generating' && (
        <div className="px-4 py-2 border-b bg-muted/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Home className="w-3 h-3" />
                {propertiesRemaining === 'unlimited' ? '∞' : propertiesRemaining} {propertiesRemaining === 1 ? 'property' : 'properties'} left
              </span>
            </div>
            {photos.length > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Camera className="w-3 h-3" />
                {photos.length} photo{photos.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Compact photo indicator (only for early steps) */}
      {photos.length > 0 && !billing && currentStep !== 'photos' && currentStep !== 'generating' && !['enhancement-choice', 'enhance-options', 'photo-instructions', 'watermark-options', 'advanced-settings', 'final-review', 'method'].includes(currentStep) && (
        <div className="px-4 py-2 border-b bg-muted/20">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Camera className="w-3 h-3" />
            <span>{photos.length} photo{photos.length !== 1 ? 's' : ''} ready</span>
          </div>
        </div>
      )}

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((message, index) => (
          <div
            key={message.id}
            className={cn(
              "flex gap-2 opacity-0 animate-fade-up",
              message.type === 'user' ? "justify-end" : "justify-start"
            )}
            style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
          >
            {message.type === 'bot' && (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 animate-scale-in">
                <Bot className="w-4 h-4 text-primary" />
              </div>
            )}
            <div
              className={cn(
                "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm transition-all duration-300",
                message.type === 'user'
                  ? "bg-primary text-primary-foreground rounded-br-sm shadow-md shadow-primary/20"
                  : "bg-muted rounded-bl-sm hover:bg-muted/80"
              )}
            >
              {message.content}
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex gap-2 animate-fade-up">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Bot className="w-4 h-4 text-primary" />
            </div>
            <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-2.5">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Step input area */}
      <div className="px-4 py-4 border-t bg-background animate-slide-up safe-area-bottom">
        {renderStepContent()}
      </div>
    </div>
  );
}
